import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Inbox, Users, Wallet, CheckCircle, MapPin, Building, Download, UserPlus, ShieldAlert, Archive, Send, Clock, FileText, Edit3, X, Save, CreditCard, Building2, Briefcase, Trash2, Search, Loader2, AlertTriangle, UserX, RotateCcw, Calendar as CalendarIcon, Mail, Plus, EyeOff, Eye, TrendingDown, Smartphone, Monitor, GitMerge, ArrowLeftRight } from 'lucide-react';
import { api, ApiError } from '../api';

const CI = { brombeer: '#85366C', orchidee: '#DC91D0', blue: '#2DBAE8', green: '#8CC53F', red: '#ED2979' };

const INITIAL_INBOX = [
  { id: 'in-1', date: '2026-05-18', time: 'N/A', type: 'hausbesuch', region: 'Cuxhaven', patient: 'Liese Müller', status: 'Wartet auf Zuweisung', origin: 'Netzwerk' },
  { id: 'in-2', date: '2026-05-19', time: '14:00', type: 'video', region: 'Online', patient: 'Heinrich Schulz', status: 'Wartet auf Zuweisung', origin: 'Netzwerk' }
];

const INITIAL_TEAM_DATA = [
  { id: 'b-1', name: 'Dein Name (Du)', role: 'Intern', type: 'Admin', regions: ['Bremen', 'Online'], rateNetwork: 0, rateSelf: 0, completedNetwork: 28, completedSelf: 14, firmierung: 'Pflege Assistent Zentrale', ikNumber: '112233445', iban: 'DE1234...', sepa: false },
  { id: 'b-2', name: 'Sabine Wagner', role: 'Externer Partner', type: 'Kooperation', regions: ['Delmenhorst', 'Diepholz'], rateNetwork: 35.00, rateSelf: 10.00, completedNetwork: 8, completedSelf: 4, firmierung: 'Pflegeberatung Wagner', ikNumber: '556677889', iban: 'DE9876...', sepa: true },
  { id: 'b-3', name: 'Thomas Weber', role: 'Externer Partner', type: 'Kooperation', regions: ['Cuxhaven', 'Osterholz'], rateNetwork: 35.00, rateSelf: 10.00, completedNetwork: 5, completedSelf: 12, firmierung: 'Weber Seniorenberatung', ikNumber: '998877665', iban: 'DE5544...', sepa: true }
];

export default function SuperAdminDashboard() {
  const [activeTab, setActiveTab] = useState('team');
  const [inbox, setInbox] = useState(INITIAL_INBOX);
  const [teamData, setTeamData] = useState(INITIAL_TEAM_DATA);

  // AS-Bremen-Abrechnung
  const [asBremenWartend, setAsBremenWartend] = useState([]);
  const [asBremenVersendet, setAsBremenVersendet] = useState([]);
  const [asBremenLoading, setAsBremenLoading] = useState(false);
  const [asBremenSyncLoading, setAsBremenSyncLoading] = useState(false);

  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [partnerFormData, setPartnerFormData] = useState(null);
  const [partnerModalTab, setPartnerModalTab] = useState('stammdaten'); 

  const assignLead = (id, beraterName) => {
    setInbox(prev => prev.filter(item => item.id !== id));
    alert(`Termin wurde erfolgreich an ${beraterName} zugewiesen.`);
  };

  const handleGenerateInvoice = (partnerName, amount) => {
    alert(`Gutschrift für ${partnerName} über ${amount} € wird als PDF generiert.`);
  };

  const ladeASBremen = async () => {
    setAsBremenLoading(true);
    try {
      const [wartend, versendet] = await Promise.all([
        api.get('/api/abrechnung/as-bremen/wartend').catch(() => []),
        api.get('/api/abrechnung/as-bremen/versendet?limit=20').catch(() => []),
      ]);
      setAsBremenWartend(wartend || []);
      setAsBremenVersendet(versendet || []);
    } finally {
      setAsBremenLoading(false);
    }
  };

  // Bei Tab-Wechsel auf 'finance' frisch laden
  useEffect(() => {
    if (activeTab === 'finance') ladeASBremen();
  }, [activeTab]);

  const handleZurueckholen = async (id, quelle) => {
    if (!window.confirm('Aus der AS-Bremen-Warteschlange zurueckziehen? Im Berater-Dashboard ist der Eintrag danach wieder bearbeitbar.')) return;
    // Digitale Cockpit-Protokolle vs. eingescannte Scan-Dokumente nutzen
    // unterschiedliche Endpoints — die Quelle steht im Listen-Eintrag.
    const url = quelle === 'scan'
      ? `/api/dokumente/${id}/zur-abrechnung`
      : `/api/protokolle/${id}/zur-abrechnung`;
    try {
      await api.del(url);
      await ladeASBremen();
    } catch (e) {
      const msg = e instanceof ApiError ? (e.detail || `Fehler ${e.status}`) : 'Fehler beim Zurueckziehen.';
      alert(msg);
    }
  };

  const forceASBremenSync = async () => {
    if (asBremenWartend.length === 0) {
      alert('Die Warteschlange ist leer — nichts zu uebermitteln.');
      return;
    }
    if (!window.confirm(`Jetzt ${asBremenWartend.length} Protokoll(e) als verschluesseltes ZIP an AS Bremen senden?`)) return;
    setAsBremenSyncLoading(true);
    try {
      const r = await api.post('/api/abrechnung/as-bremen/jetzt-senden', {});
      alert(`Gesendet: ${r.anzahl_protokolle || 0} Protokolle in einem ZIP.`);
      await ladeASBremen();
    } catch (e) {
      const msg = e instanceof ApiError ? (e.detail || `Fehler ${e.status}`) : 'Fehler beim Versand.';
      alert(msg);
    } finally {
      setAsBremenSyncLoading(false);
    }
  };

  const handleOpenAddPartner = () => {
    setPartnerFormData({
      id: 'new-' + Date.now(), isNew: true, name: '', role: 'Externer Partner', firmierung: '',
      street: '', zip: '', city: '', email: '', phone: '', ikNumber: '', ustId: '',
      iban: '', bic: '', bankName: '', sepa: true, regions: '', rateNetwork: 35.00, rateSelf: 10.00
    });
    setPartnerModalTab('stammdaten');
    setIsPartnerModalOpen(true);
  };

  const handleOpenEditPartner = (partner) => {
    setPartnerFormData({ ...partner, isNew: false, regions: partner.regions.join(', ') });
    setPartnerModalTab('stammdaten');
    setIsPartnerModalOpen(true);
  };

  const handlePartnerFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPartnerFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const savePartnerData = () => {
    const processedData = {
      ...partnerFormData,
      regions: typeof partnerFormData.regions === 'string' ? partnerFormData.regions.split(',').map(r => r.trim()) : partnerFormData.regions,
      rateNetwork: parseFloat(partnerFormData.rateNetwork),
      rateSelf: parseFloat(partnerFormData.rateSelf),
      completedNetwork: partnerFormData.isNew ? 0 : partnerFormData.completedNetwork,
      completedSelf: partnerFormData.isNew ? 0 : partnerFormData.completedSelf,
    };

    if (partnerFormData.isNew) {
      delete processedData.isNew;
      setTeamData([...teamData, processedData]);
    } else {
      delete processedData.isNew;
      setTeamData(teamData.map(p => p.id === processedData.id ? processedData : p));
    }
    setIsPartnerModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col relative">
      <header className="bg-slate-900 border-b border-slate-800 p-4 px-6 flex justify-between items-center shadow-md z-10 text-white">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md flex items-center justify-center font-bold text-white shadow-sm" style={{ backgroundColor: CI.brombeer }}>
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div><h1 className="font-bold text-lg leading-tight tracking-tight">Super-Admin Zentrale</h1><p className="text-xs text-slate-400">Mandanten- & Partnerverwaltung</p></div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right text-xs"><span className="block text-slate-400">Eingeloggt als</span><span className="font-bold">Zentrale Steuerung</span></div>
          <div className="w-10 h-10 rounded-full bg-slate-700 border-2 border-slate-600"></div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col shadow-sm">
          <div className="p-4 py-6 space-y-1">
            <button onClick={() => setActiveTab('inbox')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'inbox' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}>
              <Inbox className="w-5 h-5" /> Posteingang (Leads)
              {inbox.length > 0 && <span className="ml-auto bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full">{inbox.length}</span>}
            </button>
            <button onClick={() => setActiveTab('team')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'team' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}><Users className="w-5 h-5" /> Team & Partner</button>
            <button onClick={() => setActiveTab('kunden')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'kunden' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}><UserX className="w-5 h-5" /> Kunden verwalten</button>
            <button onClick={() => setActiveTab('papierkorb')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'papierkorb' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}><Archive className="w-5 h-5" /> Papierkorb</button>
            <button onClick={() => setActiveTab('merge')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'merge' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}><GitMerge className="w-5 h-5" /> Doppelte zusammenführen</button>
            <button onClick={() => setActiveTab('mailvorlagen')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'mailvorlagen' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}><Mail className="w-5 h-5" /> Mail-Vorlagen</button>
            <button onClick={() => setActiveTab('finance')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'finance' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}><Wallet className="w-5 h-5" /> Abrechnung & AS Bremen</button>
            <button onClick={() => setActiveTab('funnel')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'funnel' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}><TrendingDown className="w-5 h-5" /> Widget-Funnel</button>
            <button onClick={() => setActiveTab('kongress')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === 'kongress' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}><Inbox className="w-5 h-5" /> Ticketlisten</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8 bg-slate-50/50 relative">
          {activeTab === 'inbox' && (
             <div className="max-w-5xl animate-fade-in">
             <h2 className="text-2xl font-bold text-gray-800 mb-2">Zentrale Terminvergabe</h2>
             <p className="text-gray-500 mb-8">Hier landen alle Anfragen aus dem Widget, die noch keinem Berater zugeteilt wurden.</p>
             {inbox.length === 0 ? (
               <div className="bg-white rounded-2xl p-10 text-center border border-gray-200 shadow-sm">
                 <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                 <h3 className="text-xl font-bold text-gray-700">Alles erledigt!</h3>
                 <p className="text-gray-500">Es warten aktuell keine Leads auf Zuweisung.</p>
               </div>
             ) : (
               <div className="space-y-4">
                 {inbox.map((lead) => (
                   <div key={lead.id} className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow">
                     <div className="flex items-center gap-5">
                       <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${lead.type === 'hausbesuch' ? 'bg-orange-100 text-orange-600' : 'bg-purple-100 text-purple-600'}`}>
                         {lead.type === 'hausbesuch' ? <MapPin className="w-6 h-6" /> : <Building className="w-6 h-6" />}
                       </div>
                       <div>
                         <div className="flex items-center gap-2 mb-1">
                           <span className="font-bold text-gray-800 text-lg">{lead.patient}</span>
                           <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600">{lead.type === 'hausbesuch' ? 'Hausbesuch' : 'Video'}</span>
                         </div>
                         <div className="text-sm text-gray-500 flex items-center gap-4">
                           <span>Wunschdatum: {new Date(lead.date).toLocaleDateString('de-DE')} {lead.time !== 'N/A' && `um ${lead.time}`}</span>
                           <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/> Region: {lead.region}</span>
                         </div>
                       </div>
                     </div>
                     <div className="flex items-center gap-3">
                       <select className="p-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" onChange={(e) => { if(e.target.value !== "") assignLead(lead.id, e.target.options[e.target.selectedIndex].text); }}>
                         <option value="">-- Berater zuweisen --</option>
                         {teamData.map(b => (
                           <option key={b.id} value={b.id}>{b.name} ({b.regions.includes(lead.region) ? 'Passend' : 'Andere Region'})</option>
                         ))}
                       </select>
                     </div>
                   </div>
                 ))}
               </div>
             )}
           </div>
          )}

          {activeTab === 'team' && (
             <div className="max-w-5xl animate-fade-in">
             <div className="flex justify-between items-center mb-8">
               <div><h2 className="text-2xl font-bold text-gray-800 mb-1">Partner Netzwerk</h2><p className="text-gray-500">Verwalte interne Mitarbeiter und externe Kooperationspartner (B2B Stammdaten).</p></div>
               <button onClick={handleOpenAddPartner} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 transition-colors shadow-md hover:-translate-y-0.5"><UserPlus className="w-4 h-4" /> Partner hinzufügen</button>
             </div>
             <div className="grid grid-cols-1 gap-4">
               {teamData.map(member => (
                 <div key={member.id} className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow">
                   <div className="flex items-center gap-4">
                     <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-lg shrink-0">{member.name.charAt(0)}</div>
                     <div>
                       <h4 className="font-bold text-gray-800 flex items-center gap-2">{member.firmierung || member.name}{member.sepa && member.role !== 'Intern' && <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded font-bold">SEPA aktiv</span>}</h4>
                       <div className="flex items-center gap-2 mt-1 text-xs">
                         <span className={`px-2 py-0.5 rounded font-bold ${member.role === 'Intern' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>{member.role}</span>
                         {member.ikNumber && <span className="text-gray-500 flex items-center gap-1 border-l border-gray-300 pl-2">IK: {member.ikNumber}</span>}
                         <span className="text-gray-500 flex items-center gap-1 border-l border-gray-300 pl-2"><MapPin className="w-3 h-3" /> {member.regions.join(', ')}</span>
                       </div>
                     </div>
                   </div>
                   {member.role === 'Externer Partner' && (
                     <div className="text-right flex gap-6">
                       <div><div className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mb-1">Tarif: Netzwerk</div><div className="font-bold text-gray-700">{member.rateNetwork.toFixed(2)} €</div></div>
                       <div><div className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mb-1">Tarif: Eigene</div><div className="font-bold text-gray-700">{member.rateSelf.toFixed(2)} €</div></div>
                     </div>
                   )}
                   <div>
                     <button onClick={() => handleOpenEditPartner(member)} className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-1"><Edit3 className="w-4 h-4"/> Bearbeiten</button>
                   </div>
                 </div>
               ))}
             </div>
           </div>
          )}

          {activeTab === 'kunden' && (
            <KundenVerwaltung />
          )}

          {activeTab === 'papierkorb' && (
            <Papierkorb />
          )}

          {activeTab === 'merge' && (
            <KundenZusammenfuehren />
          )}

          {activeTab === 'mailvorlagen' && (
            <MailVorlagenVerwaltung />
          )}

          {activeTab === 'finance' && (
            <div className="max-w-6xl animate-fade-in">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Abrechnungen & AS Bremen</h2>
              <p className="text-gray-500 mb-8">Sammelt alle Protokolle, die im Berater-Dashboard mit „Abrechnen" markiert wurden. Mittwochs 12:00 Uhr Berlin-Zeit gehen sie als verschluesseltes ZIP an AS Bremen.</p>
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl shadow-lg p-6 mb-8 text-white flex items-center justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center shrink-0"><Archive className="w-6 h-6 text-blue-400" /></div>
                  <div>
                    <h3 className="text-xl font-bold mb-1 flex items-center gap-2">Zentrale AS Bremen Übermittlung</h3>
                    <p className="text-slate-300 text-sm max-w-xl">Alle markierten Protokolle werden mittwochs gesammelt, als verschluesseltes ZIP verpackt und an Belegeinreichung.pflegeberater@as-bremen.de geschickt.</p>
                    <div className="flex items-center gap-4 mt-3 text-xs font-bold">
                      <span className="bg-blue-500/20 text-blue-300 px-3 py-1.5 rounded-lg flex items-center gap-1 border border-blue-500/30"><Clock className="w-4 h-4" /> Naechster Sync: Mittwoch, 12:00 Uhr</span>
                      <span className="bg-green-500/20 text-green-400 px-3 py-1.5 rounded-lg flex items-center gap-1 border border-green-500/30"><FileText className="w-4 h-4" /> {asBremenLoading ? '…' : `${asBremenWartend.length} Protokoll(e) in Warteschlange`}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button onClick={forceASBremenSync} disabled={asBremenSyncLoading || asBremenWartend.length === 0} className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-xl font-bold text-sm transition-colors shadow-lg">
                    {asBremenSyncLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {asBremenSyncLoading ? 'Sendet…' : 'Jetzt manuell uebermitteln'}
                  </button>
                </div>
              </div>

              {/* Warteschlange */}
              <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2"><Wallet className="w-5 h-5" style={{color: CI.brombeer}}/> Warteschlange ({asBremenWartend.length})</h3>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-8">
                {asBremenLoading ? (
                  <div className="p-6 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Lade Warteschlange…</div>
                ) : asBremenWartend.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 italic">Keine Protokolle in der Warteschlange.</div>
                ) : (
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 border-b border-gray-200 text-slate-600">
                      <tr>
                        <th className="p-4 font-bold">Kunde</th>
                        <th className="p-4 font-bold">Quelle</th>
                        <th className="p-4 font-bold">Termin-Datum</th>
                        <th className="p-4 font-bold">Markiert seit</th>
                        <th className="p-4 font-bold text-right">Aktion</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {asBremenWartend.map(p => (
                        <tr key={p.id} className="hover:bg-slate-50/50">
                          <td className="p-4 font-medium text-gray-800">{p.kunde_name}</td>
                          <td className="p-4">
                            {p.quelle === 'scan' ? (
                              <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">Scan</span>
                            ) : (
                              <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">Digital</span>
                            )}
                          </td>
                          <td className="p-4 text-gray-600">{p.termin_datum ? new Date(p.termin_datum).toLocaleDateString('de-DE') : '—'}</td>
                          <td className="p-4 text-gray-500 text-xs">{p.zur_abrechnung_markiert_am ? new Date(p.zur_abrechnung_markiert_am).toLocaleString('de-DE') : '—'}</td>
                          <td className="p-4 text-right">
                            <div className="inline-flex items-center gap-2">
                              {p.download_url_intern && (
                                <a href={p.download_url_intern} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-md border border-blue-200"><Download className="w-3.5 h-3.5" /> PDF</a>
                              )}
                              <button onClick={() => handleZurueckholen(p.id, p.quelle)} className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-amber-50 text-amber-800 hover:bg-amber-100 rounded-md border border-amber-200" title="Zurueck zur Bearbeitung">
                                <RotateCcw className="w-3.5 h-3.5" /> Zurueck zur Bearbeitung
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Zuletzt versendet */}
              <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-600" /> Zuletzt versendet ({asBremenVersendet.length})</h3>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-8">
                {asBremenVersendet.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 italic">Noch keine Protokolle versendet.</div>
                ) : (
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 border-b border-gray-200 text-slate-600">
                      <tr>
                        <th className="p-4 font-bold">Kunde</th>
                        <th className="p-4 font-bold">Quelle</th>
                        <th className="p-4 font-bold">Termin-Datum</th>
                        <th className="p-4 font-bold">Versendet am</th>
                        <th className="p-4 font-bold">ZIP-ID</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {asBremenVersendet.map(p => (
                        <tr key={p.id} className="hover:bg-slate-50/50">
                          <td className="p-4 font-medium text-gray-800">{p.kunde_name}</td>
                          <td className="p-4">
                            {p.quelle === 'scan' ? (
                              <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">Scan</span>
                            ) : (
                              <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">Digital</span>
                            )}
                          </td>
                          <td className="p-4 text-gray-600">{p.termin_datum ? new Date(p.termin_datum).toLocaleDateString('de-DE') : '—'}</td>
                          <td className="p-4 text-gray-500 text-xs">{p.abrechnung_versendet_am ? new Date(p.abrechnung_versendet_am).toLocaleString('de-DE') : '—'}</td>
                          <td className="p-4 text-gray-400 text-xs font-mono">{p.as_bremen_zip_id || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><Wallet className="w-5 h-5 text-brombeer" style={{color: CI.brombeer}}/> Monatliche Auszahlungen (Externe Partner)</h3>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 border-b border-gray-200 text-slate-600">
                    <tr><th className="p-4 font-bold">Kooperationspartner</th><th className="p-4 font-bold text-center border-x border-gray-100 bg-blue-50/30">Netzwerk-Kunden</th><th className="p-4 font-bold text-center border-r border-gray-100 bg-green-50/30">Eigene Kunden</th><th className="p-4 font-bold text-right">Auszahlungsbetrag</th><th className="p-4 font-bold text-right">Aktion</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {teamData.filter(m => m.role === 'Externer Partner').map(partner => {
                      const totalNetwork = partner.completedNetwork * partner.rateNetwork;
                      const totalSelf = partner.completedSelf * partner.rateSelf;
                      const totalAmount = totalNetwork + totalSelf;
                      return (
                        <tr key={partner.id} className="hover:bg-slate-50/50">
                          <td className="p-4"><span className="font-bold text-gray-800">{partner.name}</span>{!partner.sepa && <span className="block text-[10px] text-red-500 font-bold mt-1">Kein SEPA-Mandat hinterlegt!</span>}</td>
                          <td className="p-4 text-center border-x border-gray-100 bg-blue-50/10"><span className="font-bold text-blue-600">{partner.completedNetwork} Termine</span><div className="text-xs text-gray-400 mt-0.5">à {partner.rateNetwork.toFixed(2)} € = {totalNetwork.toFixed(2)} €</div></td>
                          <td className="p-4 text-center border-r border-gray-100 bg-green-50/10"><span className="font-bold text-green-600">{partner.completedSelf} Termine</span><div className="text-xs text-gray-400 mt-0.5">à {partner.rateSelf.toFixed(2)} € = {totalSelf.toFixed(2)} €</div></td>
                          <td className="p-4 text-right font-bold text-gray-800 text-base">{totalAmount.toFixed(2)} €</td>
                          <td className="p-4 text-right"><button onClick={() => handleGenerateInvoice(partner.name, totalAmount.toFixed(2))} className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg text-xs font-bold hover:bg-brombeer transition-colors shadow-sm"><Download className="w-4 h-4" /> Gutschrift</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {activeTab === 'funnel' && (
            <WidgetFunnelView />
          )}
          {activeTab === 'kongress' && (
            <KongressView />
          )}
        </div>
      </div>

      {isPartnerModalOpen && partnerFormData && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 pb-0 border-b border-gray-100 bg-slate-50 flex flex-col shrink-0">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">{partnerFormData.isNew ? <UserPlus className="w-5 h-5 text-brombeer" style={{ color: CI.brombeer }} /> : <Edit3 className="w-5 h-5 text-brombeer" style={{ color: CI.brombeer }} />}{partnerFormData.isNew ? 'Neuen Kooperationspartner anlegen' : 'Partner-Profil bearbeiten'}</h2>
                <button onClick={() => setIsPartnerModalOpen(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-2 rounded-lg transition-colors"><X className="w-5 h-5"/></button>
              </div>
              <div className="flex gap-6 overflow-x-auto custom-scrollbar">
                <button onClick={() => setPartnerModalTab('stammdaten')} className={`pb-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${partnerModalTab === 'stammdaten' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: partnerModalTab === 'stammdaten' ? CI.brombeer : 'transparent' }}><Building2 className="w-4 h-4" /> B2B Stammdaten & IK</button>
                <button onClick={() => setPartnerModalTab('finanzen')} className={`pb-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${partnerModalTab === 'finanzen' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: partnerModalTab === 'finanzen' ? CI.brombeer : 'transparent' }}><CreditCard className="w-4 h-4" /> Bankverbindung & SEPA</button>
                <button onClick={() => setPartnerModalTab('netzwerk')} className={`pb-3 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${partnerModalTab === 'netzwerk' ? 'border-brombeer text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} style={{ borderColor: partnerModalTab === 'netzwerk' ? CI.brombeer : 'transparent' }}><Briefcase className="w-4 h-4" /> Tarife & Gebiete</button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1 bg-white">
              {partnerModalTab === 'stammdaten' && (
                <div className="space-y-6 animate-fade-in">
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Unternehmensdaten</h4>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Firmierung / Firmenname *</label><input type="text" name="firmierung" value={partnerFormData.firmierung || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="z.B. Pflegeberatung Müller GmbH" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Ansprechpartner Vorname</label><input type="text" name="name" value={partnerFormData.name.split(' ')[0] || ''} onChange={(e) => setPartnerFormData({...partnerFormData, name: e.target.value + ' ' + (partnerFormData.name.split(' ')[1] || '')})} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Nachname</label><input type="text" value={partnerFormData.name.split(' ')[1] || ''} onChange={(e) => setPartnerFormData({...partnerFormData, name: (partnerFormData.name.split(' ')[0] || '') + ' ' + e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Gesundheitswesen & Steuern</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-1">IK-Nummer <span className="text-red-500">*</span></label><input type="text" name="ikNumber" value={partnerFormData.ikNumber || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="9-stellige Institutionskennzeichen" /><p className="text-[10px] text-gray-500 mt-1">Zwingend erforderlich für die Abrechnung über AS Bremen.</p></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Umsatzsteuer-ID (USt-IdNr.)</label><input type="text" name="ustId" value={partnerFormData.ustId || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="DE..." /></div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Adresse & Kontakt</h4>
                    <div className="grid grid-cols-6 gap-4">
                      <div className="col-span-6"><label className="block text-sm font-medium text-gray-700 mb-1">Straße & Hausnummer</label><input type="text" name="street" value={partnerFormData.street || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                      <div className="col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">PLZ</label><input type="text" name="zip" value={partnerFormData.zip || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                      <div className="col-span-4"><label className="block text-sm font-medium text-gray-700 mb-1">Ort</label><input type="text" name="city" value={partnerFormData.city || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                      <div className="col-span-3"><label className="block text-sm font-medium text-gray-700 mb-1">Geschäftliche E-Mail</label><input type="email" name="email" value={partnerFormData.email || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                      <div className="col-span-3"><label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label><input type="tel" name="phone" value={partnerFormData.phone || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                    </div>
                  </div>
                </div>
              )}

              {partnerModalTab === 'finanzen' && (
                <div className="space-y-6 animate-fade-in h-full">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl mb-6 flex gap-3 items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                    <div><h4 className="font-bold text-green-800 text-sm">Verbindliches SEPA-Lastschriftmandat</h4><p className="text-xs text-green-700 mt-1">Um administrative Aufwände und Mahnläufe im Netzwerk zu vermeiden, erfolgt der Einzug der Netzwerk-Gebühren bei Partnern zwingend per SEPA-Basislastschrift. Der Partner muss hierzu vorab ein Mandat unterzeichnen.</p></div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Bankverbindung</h4>
                    <div className="space-y-4">
                      <div><label className="block text-sm font-bold text-gray-700 mb-1">Kontoinhaber</label><input type="text" value={partnerFormData.firmierung || partnerFormData.name || ''} readOnly className="w-full p-2.5 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-500" /></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2"><label className="block text-sm font-bold text-gray-700 mb-1">IBAN *</label><input type="text" name="iban" value={partnerFormData.iban || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 uppercase font-mono tracking-wider" placeholder="DE..." /></div>
                        <div><label className="block text-sm font-medium text-gray-700 mb-1">BIC</label><input type="text" name="bic" value={partnerFormData.bic || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 uppercase font-mono tracking-wider" /></div>
                        <div><label className="block text-sm font-medium text-gray-700 mb-1">Name der Bank</label><input type="text" name="bankName" value={partnerFormData.bankName || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" /></div>
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-gray-100">
                    <label className="flex items-start gap-3 cursor-pointer p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                      <input type="checkbox" name="sepa" checked={partnerFormData.sepa} onChange={handlePartnerFormChange} className="mt-1 w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700"><strong className="block text-gray-900 mb-0.5">Schriftliches SEPA-Mandat liegt vor</strong>Ich bestätige hiermit, dass der Partner das Formular zum Einzug von SEPA-Basislastschriften unterschrieben in der Zentrale eingereicht hat. Ohne diesen Haken ist eine Systemfreigabe nicht möglich.</span>
                    </label>
                  </div>
                </div>
              )}

              {partnerModalTab === 'netzwerk' && (
                <div className="space-y-6 animate-fade-in h-full">
                  <div>
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Einsatzgebiete & Sichtbarkeit</h4>
                    <p className="text-xs text-gray-500 mb-3">In welchen Regionen soll dieser Berater bei einer Kundenanfrage für Hausbesuche vorgeschlagen werden?</p>
                    <input type="text" name="regions" value={partnerFormData.regions || ''} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="z.B. Bremen, Delmenhorst, Verden (Kommagetrennt)" />
                    <p className="text-[10px] text-gray-400 mt-1">Hinweis: "Online" als Tag setzen, wenn Videoberatung angeboten wird.</p>
                  </div>
                  <div className="pt-4 border-t border-gray-100">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Abrechnungsmodell & Tarife</h4>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                        <label className="block text-sm font-bold text-blue-900 mb-1">Tarif 1: System-Leads</label><p className="text-xs text-blue-700 mb-3">Vergütung an den Partner, wenn das Netzwerk den Kunden vermittelt hat.</p>
                        <div className="relative"><input type="number" step="0.01" name="rateNetwork" value={partnerFormData.rateNetwork} onChange={handlePartnerFormChange} className="w-full p-2.5 pl-8 border border-gray-300 rounded-lg text-sm font-bold focus:ring-2 focus:ring-blue-500" /><span className="absolute left-3 top-2.5 font-bold text-gray-500">€</span></div>
                      </div>
                      <div className="p-4 bg-green-50 border border-green-100 rounded-xl">
                        <label className="block text-sm font-bold text-green-900 mb-1">Tarif 2: Eigene Kunden</label><p className="text-xs text-green-700 mb-3">Vergütung an den Partner, wenn er einen eigenen Kunden in unser System zur AS-Bremen Abrechnung einpflegt.</p>
                        <div className="relative"><input type="number" step="0.01" name="rateSelf" value={partnerFormData.rateSelf} onChange={handlePartnerFormChange} className="w-full p-2.5 pl-8 border border-gray-300 rounded-lg text-sm font-bold focus:ring-2 focus:ring-green-500" /><span className="absolute left-3 top-2.5 font-bold text-gray-500">€</span></div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rolle im System</label>
                    <select name="role" value={partnerFormData.role} onChange={handlePartnerFormChange} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500">
                      <option value="Externer Partner">Externer Kooperationspartner</option><option value="Intern">Festangestellter Mitarbeiter (Intern)</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 border-t border-gray-100 bg-slate-50 flex justify-between items-center rounded-b-2xl shrink-0">
              <div className="text-xs text-gray-500">
                {partnerFormData.sepa ? <span className="text-green-600 font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3"/> SEPA-Mandat aktiv</span> : <span className="text-red-500 font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3"/> Kein SEPA bestätigt</span>}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setIsPartnerModalOpen(false)} className="px-5 py-2.5 rounded-lg font-bold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm">Abbrechen</button>
                <button onClick={savePartnerData} className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-white shadow-md hover:-translate-y-0.5 transition-all" style={{ backgroundColor: CI.blue }}><Save className="w-4 h-4" />{partnerFormData.isNew ? 'Partner anlegen' : 'Änderungen speichern'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ==========================================================================
//  Kunden-Verwaltung mit Filter, Mehrfach-Auswahl und Loeschen
// ==========================================================================
function KundenVerwaltung() {
  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState(null);

  // Filter-State
  const [suche, setSuche] = useState('');
  const [angelegtVon, setAngelegtVon] = useState('');   // YYYY-MM-DD
  const [angelegtBis, setAngelegtBis] = useState('');

  // Selektion (Set von kunde.id)
  const [selektion, setSelektion] = useState(new Set());
  const [isLoeschend, setIsLoeschend] = useState(false);

  const ladeKunden = async () => {
    setLoading(true);
    setFehler(null);
    try {
      const liste = await api.get('/api/kunden');
      setKunden(liste || []);
      setSelektion(new Set());  // Selektion leeren nach Reload
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      setFehler(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { ladeKunden(); }, []);

  // Filter anwenden
  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return kunden.filter(k => {
      if (q) {
        const haystackKunde = `${k.firstName || ''} ${k.lastName || ''}`.toLowerCase();
        let trifft = haystackKunde.includes(q);
        if (!trifft && k.relative) {
          const rel = `${k.relative.firstName || ''} ${k.relative.lastName || ''}`.toLowerCase();
          if (rel.includes(q)) trifft = true;
        }
        if (!trifft) return false;
      }
      if (angelegtVon || angelegtBis) {
        const created = (k.created_at || '').slice(0, 10);
        if (!created) return false;
        if (angelegtVon && created < angelegtVon) return false;
        if (angelegtBis && created > angelegtBis) return false;
      }
      return true;
    });
  }, [kunden, suche, angelegtVon, angelegtBis]);

  const toggleSelektion = (id) => {
    setSelektion(prev => {
      const neu = new Set(prev);
      if (neu.has(id)) neu.delete(id); else neu.add(id);
      return neu;
    });
  };
  const selektionAllUmschalten = () => {
    if (selektion.size === gefiltert.length && gefiltert.length > 0) {
      setSelektion(new Set());
    } else {
      setSelektion(new Set(gefiltert.map(k => k.id)));
    }
  };

  const handleLoeschenEinzeln = async (kunde) => {
    if (!confirm(`Diesen Kunden in den Papierkorb verschieben?\n\n${kunde.lastName}, ${kunde.firstName}\n\nDie zugehörigen Termine, Protokolle, ToDos und Diagnosen bleiben verknüpft und kommen bei Wiederherstellung sofort wieder mit.`)) return;
    setIsLoeschend(true);
    try {
      await api.del(`/api/kunden/${kunde.id}`);
      await ladeKunden();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `Fehler ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      alert(typeof msg === 'string' ? msg : 'Löschen fehlgeschlagen.');
    } finally {
      setIsLoeschend(false);
    }
  };

  const handleBulkLoeschen = async () => {
    const ids = Array.from(selektion);
    if (!ids.length) return;
    if (!confirm(`${ids.length} Kunden in den Papierkorb verschieben?\n\nDie zugehörigen Termine, Protokolle, ToDos und Diagnosen bleiben verknüpft und kommen bei Wiederherstellung sofort wieder mit.`)) return;
    setIsLoeschend(true);
    try {
      const ergebnis = await api.post('/api/kunden/bulk-delete', { ids });
      alert(`${ergebnis.geloescht} Kunden gelöscht.${ergebnis.nicht_gefunden > 0 ? ` ${ergebnis.nicht_gefunden} nicht gefunden (vermutlich zwischenzeitlich entfernt).` : ''}`);
      await ladeKunden();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `Fehler ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      alert(typeof msg === 'string' ? msg : 'Bulk-Löschen fehlgeschlagen.');
    } finally {
      setIsLoeschend(false);
    }
  };

  return (
    <div className="max-w-6xl animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Kunden verwalten</h2>
      <p className="text-gray-500 mb-6">
        Filter nach Name, Angehörigen oder Anlage-Datum. Einzeln per Trash-Icon
        oder mehrere selektieren und gemeinsam löschen.
        <span className="ml-1 text-blue-700 font-semibold">Gelöschte Einträge landen im Papierkorb und können von dort wiederhergestellt oder endgültig gelöscht werden.</span>
      </p>

      {/* Filter-Leiste */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-4 flex flex-col md:flex-row gap-3 items-stretch md:items-end">
        <div className="flex-1">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Suche (Name / Vorname / Angehörige)</label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="z.B. Mustermann oder Christiane Sauret"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Angelegt von</label>
          <input
            type="date" value={angelegtVon} onChange={(e) => setAngelegtVon(e.target.value)}
            className="block mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">bis</label>
          <input
            type="date" value={angelegtBis} onChange={(e) => setAngelegtBis(e.target.value)}
            className="block mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-300"
          />
        </div>
        <button
          onClick={() => { setSuche(''); setAngelegtVon(''); setAngelegtBis(''); }}
          className="px-3 py-2 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
          title="Alle Filter zurücksetzen"
        >Filter leeren</button>
      </div>

      {/* Aktions-Leiste: nur sichtbar bei Selektion */}
      {selektion.size > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-800">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-bold text-sm">{selektion.size} Kunden ausgewählt</span>
          </div>
          <button
            onClick={handleBulkLoeschen}
            disabled={isLoeschend}
            className="flex items-center gap-2 px-5 py-2 rounded-lg font-bold text-white shadow-sm bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {isLoeschend ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {selektion.size} ausgewählte löschen
          </button>
        </div>
      )}

      {/* Tabelle */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-12 flex items-center justify-center text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Kunden…
          </div>
        ) : fehler ? (
          <div className="bg-red-50 border-b border-red-200 text-red-700 rounded p-4 text-sm">{fehler}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="w-12 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selektion.size === gefiltert.length && gefiltert.length > 0}
                    onChange={selektionAllUmschalten}
                    title="Alle (gefilterten) auswählen / abwählen"
                  />
                </th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Adresse</th>
                <th className="px-3 py-2 text-left">Angehörige</th>
                <th className="px-3 py-2 text-left">V.-Nr.</th>
                <th className="px-3 py-2 text-left">Angelegt</th>
                <th className="w-12 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {gefiltert.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-3 py-12 text-center text-gray-400 italic">
                    {kunden.length === 0 ? 'Keine Kunden in der Datenbank.' : 'Keine Treffer für die aktuellen Filter.'}
                  </td>
                </tr>
              )}
              {gefiltert.slice(0, 200).map(k => (
                <tr key={k.id} className={selektion.has(k.id) ? 'bg-red-50' : 'hover:bg-gray-50'}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selektion.has(k.id)}
                      onChange={() => toggleSelektion(k.id)}
                    />
                  </td>
                  <td className="px-3 py-2 font-bold text-gray-800">{k.lastName || '—'}, {k.firstName || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{k.zip || ''} {k.city || ''}</td>
                  <td className="px-3 py-2 text-gray-600">{k.relative ? `${k.relative.firstName || ''} ${k.relative.lastName || ''}`.trim() : '—'}</td>
                  <td className="px-3 py-2 text-gray-600 font-mono text-xs">{k.insuranceNumber || '—'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">
                    {k.created_at ? new Date(k.created_at).toLocaleDateString('de-DE') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => handleLoeschenEinzeln(k)}
                      disabled={isLoeschend}
                      className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-30"
                      title="Diesen Kunden löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {gefiltert.length > 200 && (
          <div className="bg-amber-50 border-t border-amber-200 px-3 py-2 text-xs text-amber-800">
            Es werden nur die ersten 200 Treffer angezeigt — bitte Filter verfeinern.
          </div>
        )}
      </div>

      {!loading && !fehler && (
        <p className="text-xs text-gray-500 mt-3 text-right">
          {gefiltert.length} von {kunden.length} Kunden angezeigt
        </p>
      )}
    </div>
  );
}


// ==========================================================================
// Papierkorb — gelöschte Kunden mit Wiederherstellen + Endgültig-Löschen
// ==========================================================================
function Papierkorb() {
  const [eintraege, setEintraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const laden = async () => {
    setLoading(true);
    setFehler(null);
    try {
      const liste = await api.get('/api/kunden/papierkorb');
      setEintraege(Array.isArray(liste) ? liste : []);
    } catch (e) {
      const msg = e instanceof ApiError ? (e.detail || `HTTP ${e.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      setFehler(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { laden(); }, []);

  const wiederherstellen = async (k) => {
    if (!confirm(`Diesen Kunden wiederherstellen?\n\n${k.lastName}, ${k.firstName}\n\nAlle verknüpften Termine, Protokolle, ToDos und Diagnosen werden sofort wieder sichtbar.`)) return;
    setBusyId(k.id);
    try {
      await api.post(`/api/kunden/${k.id}/wiederherstellen`);
      setEintraege(prev => prev.filter(e => e.id !== k.id));
    } catch (e) {
      alert(e?.detail || 'Wiederherstellen fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const endgueltigLoeschen = async (k) => {
    if (!confirm(`ENDGÜLTIG löschen — kein Zurück mehr.\n\n${k.lastName}, ${k.firstName}\n\nDamit werden auch ALLE zugehörigen Termine (${k.anzahl_termine || 0}), Protokolle (${k.anzahl_protokolle || 0}), Dokumente (${k.anzahl_dokumente || 0}), ToDos und Diagnosen physisch aus der Datenbank entfernt.`)) return;
    setBusyId(k.id);
    try {
      await api.del(`/api/kunden/${k.id}/endgueltig`);
      setEintraege(prev => prev.filter(e => e.id !== k.id));
    } catch (e) {
      alert(e?.detail || 'Endgültiges Löschen fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const formatDatumZeit = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div className="max-w-6xl animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-2">
        <Archive className="w-6 h-6 text-gray-500" /> Papierkorb
      </h2>
      <p className="text-gray-500 mb-6">
        Hier liegen Kunden, die im Bereich „Kunden verwalten" gelöscht wurden.
        Die zugehörigen Termine, Protokolle und Dokumente sind nicht weg — bei
        Wiederherstellung sind sie sofort wieder sichtbar.
        <span className="ml-1 text-red-600 font-semibold">Endgültig löschen ist unwiderruflich.</span>
      </p>

      {fehler && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {fehler}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-400 border border-gray-200">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Lade Papierkorb…
        </div>
      ) : eintraege.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center border border-gray-200 shadow-sm">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-700">Papierkorb ist leer</h3>
          <p className="text-gray-500">Kein gelöschter Kunde wartet auf Aufräumen.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Name</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Adresse</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">V.-Nr.</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Verknüpft</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Gelöscht am</th>
                <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {eintraege.map(k => (
                <tr key={k.id} className="hover:bg-gray-50">
                  <td className="px-5 py-4">
                    <div className="font-bold text-gray-800">{k.lastName || '—'}, {k.firstName || '—'}</div>
                    {k.dob && <div className="text-xs text-gray-500">geb. {k.dob}</div>}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">
                    {[k.zip, k.city].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className="px-5 py-4 text-sm font-mono text-gray-600">
                    {k.insuranceNumber || '—'}
                  </td>
                  <td className="px-5 py-4 text-xs text-gray-600">
                    {k.anzahl_termine || 0} Termine · {k.anzahl_protokolle || 0} Protokolle · {k.anzahl_dokumente || 0} Doku
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600 flex items-center gap-1">
                    <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                    {formatDatumZeit(k.geloescht_am)}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => wiederherstellen(k)}
                        disabled={busyId === k.id}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                        title="Kunde wiederherstellen"
                      >
                        {busyId === k.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                        Wiederherstellen
                      </button>
                      <button
                        onClick={() => endgueltigLoeschen(k)}
                        disabled={busyId === k.id}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                        title="Endgültig aus der Datenbank entfernen"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Endgültig löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-right">
            {eintraege.length} {eintraege.length === 1 ? 'Eintrag' : 'Einträge'} im Papierkorb
          </div>
        </div>
      )}
    </div>
  );
}

const PLATZHALTER_HINWEISE = [
  { key: 'anrede', beschreibung: "Anrede des Patienten — 'Frau' / 'Herr'" },
  { key: 'vorname', beschreibung: 'Vorname des Patienten' },
  { key: 'nachname', beschreibung: 'Nachname des Patienten' },
  { key: 'name', beschreibung: 'Voller Name (Vorname + Nachname)' },
  { key: 'strasse', beschreibung: 'Strasse + Hausnummer' },
  { key: 'plz', beschreibung: 'Postleitzahl — z.B. fuer den BKK-Pflegefinder-Link' },
  { key: 'ort', beschreibung: 'Wohnort' },
  { key: 'pflegegrad', beschreibung: "z.B. 'Pflegegrad 2' (leer wenn nicht gesetzt)" },
  { key: 'angehoeriger_anrede', beschreibung: 'Anrede des primaeren Angehoerigen' },
  { key: 'angehoeriger_vorname', beschreibung: 'Vorname des Angehoerigen' },
  { key: 'angehoeriger_nachname', beschreibung: 'Nachname des Angehoerigen' },
  { key: 'angehoeriger_name', beschreibung: 'Voller Name des Angehoerigen' },
  { key: 'bkk_pflegefinder_link', beschreibung: 'Such-URL fuer Entlastungsleistungen, mit PLZ vorbefuellt' },
];

const LEERE_VORLAGE = {
  id: null,
  titel: '',
  kategorie: '',
  body_template: '',
  sortierung: 0,
  aktiv: true,
};

function MailVorlagenVerwaltung() {
  const [vorlagen, setVorlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [modalDaten, setModalDaten] = useState(null); // null = zu, sonst Form-State
  const [busyId, setBusyId] = useState(null);

  const laden = async () => {
    setLoading(true);
    setFehler(null);
    try {
      const liste = await api.get('/api/mail-vorlagen?inklusive_inaktiv=true');
      setVorlagen(Array.isArray(liste) ? liste : []);
    } catch (e) {
      const msg = e instanceof ApiError ? (e.detail || `HTTP ${e.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      setFehler(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { laden(); }, []);

  const oeffneNeu = () => setModalDaten({ ...LEERE_VORLAGE });
  const oeffneEdit = (v) => setModalDaten({
    id: v.id,
    titel: v.titel || '',
    kategorie: v.kategorie || '',
    body_template: v.body_template || '',
    sortierung: v.sortierung ?? 0,
    aktiv: v.aktiv !== false,
  });

  const speichern = async () => {
    if (!modalDaten) return;
    const titel = (modalDaten.titel || '').trim();
    const body = (modalDaten.body_template || '').trim();
    if (!titel || !body) {
      alert('Titel und Inhalt sind Pflicht.');
      return;
    }
    const payload = {
      titel,
      kategorie: (modalDaten.kategorie || '').trim() || null,
      body_template: body,
      sortierung: Number(modalDaten.sortierung) || 0,
      aktiv: modalDaten.aktiv !== false,
    };
    try {
      if (modalDaten.id) {
        await api.put(`/api/mail-vorlagen/${modalDaten.id}`, payload);
      } else {
        await api.post('/api/mail-vorlagen', payload);
      }
      setModalDaten(null);
      await laden();
    } catch (e) {
      alert(e?.detail || 'Speichern fehlgeschlagen.');
    }
  };

  const deaktivieren = async (v) => {
    if (!confirm(`Vorlage „${v.titel}" deaktivieren?\n\nSie verschwindet aus der Sidebar im Mail-Editor, bleibt aber hier sichtbar und kann jederzeit wieder aktiviert werden.`)) return;
    setBusyId(v.id);
    try {
      await api.del(`/api/mail-vorlagen/${v.id}`);
      await laden();
    } catch (e) {
      alert(e?.detail || 'Deaktivieren fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const reaktivieren = async (v) => {
    setBusyId(v.id);
    try {
      await api.put(`/api/mail-vorlagen/${v.id}`, { titel: v.titel, body_template: v.body_template, aktiv: true });
      await laden();
    } catch (e) {
      alert(e?.detail || 'Reaktivieren fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  // Gruppierung nach Kategorie — Reihenfolge kommt schon vom Backend.
  const gruppen = useMemo(() => {
    const map = new Map();
    for (const v of vorlagen) {
      const key = v.kategorie || '— Ohne Kategorie —';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(v);
    }
    return Array.from(map.entries());
  }, [vorlagen]);

  return (
    <div className="max-w-6xl animate-fade-in">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-1 flex items-center gap-2">
            <Mail className="w-6 h-6 text-gray-500" /> Mail-Vorlagen
          </h2>
          <p className="text-gray-500">
            Textbausteine fuer den Mail-Editor in der Kundenakte. Aktive Vorlagen
            erscheinen in der Sidebar des Mail-Tabs und werden mit Platzhaltern
            wie <code className="bg-gray-100 px-1 rounded text-xs">{'{{vorname}}'}</code>{' '}
            beim Einfuegen aus dem Kundendatensatz befuellt.
          </p>
        </div>
        <button
          onClick={oeffneNeu}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 transition-colors shadow-md hover:-translate-y-0.5 shrink-0"
        >
          <Plus className="w-4 h-4" /> Neue Vorlage
        </button>
      </div>

      {fehler && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {fehler}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-400 border border-gray-200">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Lade Vorlagen…
        </div>
      ) : vorlagen.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center border border-gray-200 shadow-sm">
          <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-700">Noch keine Vorlagen angelegt</h3>
          <p className="text-gray-500 mb-4">Lege die erste Mail-Vorlage an, damit sie in der Sidebar des Mail-Editors erscheint.</p>
          <button onClick={oeffneNeu} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold text-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Erste Vorlage anlegen
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {gruppen.map(([kategorie, eintraege]) => (
            <div key={kategorie}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 ml-1">{kategorie}</h3>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 divide-y divide-gray-100">
                {eintraege.map(v => (
                  <div key={v.id} className={`p-4 flex items-start gap-4 ${v.aktiv ? '' : 'bg-gray-50/60'}`}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${v.aktiv ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                      <Mail className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-bold ${v.aktiv ? 'text-gray-800' : 'text-gray-500'}`}>{v.titel}</span>
                        {!v.aktiv && (
                          <span className="bg-gray-200 text-gray-600 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">inaktiv</span>
                        )}
                        <span className="text-[10px] text-gray-400 ml-auto">Sort: {v.sortierung ?? 0}</span>
                      </div>
                      <p className="text-sm text-gray-500 line-clamp-2 whitespace-pre-wrap break-words">{v.body_template}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => oeffneEdit(v)}
                        className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-1"
                        title="Bearbeiten"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      {v.aktiv ? (
                        <button
                          onClick={() => deaktivieren(v)}
                          disabled={busyId === v.id}
                          className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg text-sm font-bold transition-colors disabled:opacity-50"
                          title="Deaktivieren"
                        >
                          {busyId === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      ) : (
                        <button
                          onClick={() => reaktivieren(v)}
                          disabled={busyId === v.id}
                          className="text-emerald-600 hover:bg-emerald-50 p-2 rounded-lg text-sm font-bold transition-colors disabled:opacity-50"
                          title="Wieder aktivieren"
                        >
                          {busyId === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalDaten && (
        <VorlagenModal
          daten={modalDaten}
          setDaten={setModalDaten}
          onSpeichern={speichern}
          onAbbrechen={() => setModalDaten(null)}
        />
      )}
    </div>
  );
}

function VorlagenModal({ daten, setDaten, onSpeichern, onAbbrechen }) {
  const istNeu = !daten.id;
  const setFeld = (feld, wert) => setDaten(prev => ({ ...prev, [feld]: wert }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            {istNeu ? 'Neue Mail-Vorlage anlegen' : 'Mail-Vorlage bearbeiten'}
          </h3>
          <button onClick={onAbbrechen} className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Titel *</label>
              <input
                type="text"
                value={daten.titel}
                onChange={e => setFeld('titel', e.target.value)}
                placeholder="z.B. Anbieter Entlastungsleistungen"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Kategorie</label>
              <input
                type="text"
                value={daten.kategorie}
                onChange={e => setFeld('kategorie', e.target.value)}
                placeholder="z.B. Entlastungsleistungen"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Sortierung</label>
              <input
                type="number"
                value={daten.sortierung}
                onChange={e => setFeld('sortierung', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-[11px] text-gray-400 mt-1">Niedriger = weiter oben in der Sidebar</p>
            </div>
            <div className="md:col-span-2 flex items-center gap-2 pb-1">
              <input
                type="checkbox"
                id="vorlage-aktiv"
                checked={daten.aktiv !== false}
                onChange={e => setFeld('aktiv', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="vorlage-aktiv" className="text-sm text-gray-700">
                Aktiv — in der Sidebar des Mail-Editors anzeigen
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Inhalt *</label>
            <textarea
              value={daten.body_template}
              onChange={e => setFeld('body_template', e.target.value)}
              rows={12}
              placeholder={"Wie heute besprochen, finden Sie hier ...\n\nMit freundlichen Gruessen"}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Platzhalter wie <code className="bg-gray-100 px-1 rounded">{'{{vorname}}'}</code> werden beim Einfuegen
              aus dem Kundendatensatz ersetzt. Unbekannte Platzhalter bleiben als Text stehen — so siehst Du im Editor,
              was noch von Hand angepasst werden muss.
            </p>
          </div>

          <details className="bg-gray-50 rounded-lg border border-gray-200">
            <summary className="px-4 py-2.5 cursor-pointer text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-lg">
              Verfuegbare Platzhalter ({PLATZHALTER_HINWEISE.length})
            </summary>
            <div className="px-4 pb-3 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              {PLATZHALTER_HINWEISE.map(p => (
                <div key={p.key} className="flex gap-2">
                  <code className="bg-white border border-gray-200 px-1.5 py-0.5 rounded text-blue-700 font-mono shrink-0 h-fit">{`{{${p.key}}}`}</code>
                  <span className="text-gray-600">{p.beschreibung}</span>
                </div>
              ))}
            </div>
          </details>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onAbbrechen}
            className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-200 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={onSpeichern}
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-colors shadow-sm"
          >
            <Save className="w-4 h-4" />
            {istNeu ? 'Anlegen' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ==========================================================================
// Widget-Funnel-View — Conversion-Tracking fuer das Buchungs-Widget
// ==========================================================================
function WidgetFunnelView() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [tage, setTage] = useState(30);
  const [quelleFilter, setQuelleFilter] = useState('');  // '' = alle
  const [geraetFilter, setGeraetFilter] = useState('');  // '' = alle

  const reload = async () => {
    setIsLoading(true);
    setFehler(null);
    try {
      const params = new URLSearchParams({ tage: String(tage) });
      if (quelleFilter) params.append('quelle', quelleFilter);
      if (geraetFilter) params.append('geraet', geraetFilter);
      const data = await api.get(`/api/widget/funnel-stats?${params.toString()}`);
      setStats(data);
    } catch (e) {
      setFehler(e?.detail || 'Konnte Statistik nicht laden.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tage, quelleFilter, geraetFilter]);

  const start = stats?.funnel?.[0]?.anzahl || 0;

  // Drop-Off-Schritt mit groesstem Abbruch finden (rot markieren).
  const groessterDropIdx = (() => {
    if (!stats?.funnel) return -1;
    let maxDrop = 0, idx = -1;
    stats.funnel.forEach((f, i) => {
      if (f.drop_off_pct && f.drop_off_pct > maxDrop) {
        maxDrop = f.drop_off_pct;
        idx = i;
      }
    });
    return idx;
  })();

  // Sparkline-Max fuer Skalierung.
  const sparkMax = Math.max(1, ...(stats?.tagesreihe || []).map(t => t.anzahl));

  return (
    <div className="max-w-6xl animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Widget-Funnel</h2>
      <p className="text-gray-500 mb-6">
        Conversion-Tracking fuer das Buchungs-Widget. Anonyme Session-IDs aus dem Browser —
        keine personenbezogenen Daten, kein User-Tracking ueber Sessions hinaus.
      </p>

      {/* Filter-Leiste */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-gray-500 uppercase">Zeitraum:</span>
          {[7, 30, 90, 365].map(d => (
            <button
              key={d}
              onClick={() => setTage(d)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                tage === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {d === 365 ? '1 Jahr' : `${d} Tage`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs font-bold text-gray-500 uppercase">Quelle:</span>
          <select
            value={quelleFilter}
            onChange={(e) => setQuelleFilter(e.target.value)}
            className="px-3 py-1.5 rounded-md text-xs font-medium border border-gray-200 bg-white"
          >
            <option value="">Alle</option>
            <option value="pflege-dschungel.de">pflege-dschungel.de</option>
            <option value="spa-pflegeberatung.de">spa-pflegeberatung.de</option>
            <option value="direkt">Direkt</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-gray-500 uppercase">Geraet:</span>
          {[
            { v: '', label: 'Alle', icon: null },
            { v: 'mobile', label: 'Mobile', icon: Smartphone },
            { v: 'desktop', label: 'Desktop', icon: Monitor },
          ].map(({ v, label, icon: Icon }) => (
            <button
              key={v || 'alle'}
              onClick={() => setGeraetFilter(v)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                geraetFilter === v ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {label}
            </button>
          ))}
        </div>
      </div>

      {fehler && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 text-sm">
          {fehler}
        </div>
      )}

      {isLoading && !stats && (
        <div className="text-center text-gray-400 py-12 text-sm">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Lade Daten…
        </div>
      )}

      {stats && start === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
          Keine Widget-Aufrufe im gewaehlten Zeitfenster. Tracking-Daten beginnen ab Live-Schaltung (heute).
        </div>
      )}

      {stats && start > 0 && (
        <>
          {/* Tages-Sparkline */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-700">Widget-Aufrufe pro Tag</h3>
              <span className="text-xs text-gray-500">{start} Sessions gesamt</span>
            </div>
            <div className="flex items-end gap-0.5 h-20">
              {stats.tagesreihe.map((t, i) => (
                <div key={i} className="flex-1 flex flex-col justify-end" title={`${t.tag}: ${t.anzahl} Aufrufe`}>
                  <div
                    className="bg-blue-500 rounded-t hover:bg-blue-600 transition-colors min-h-[2px]"
                    style={{ height: `${(t.anzahl / sparkMax) * 100}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-gray-400">
              <span>{stats.tagesreihe[0]?.tag}</span>
              <span>{stats.tagesreihe[stats.tagesreihe.length - 1]?.tag}</span>
            </div>
          </div>

          {/* Funnel-Balken */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Conversion-Funnel</h3>
            <div className="space-y-2">
              {stats.funnel.map((f, i) => {
                const breite = start > 0 ? (f.anzahl / start) * 100 : 0;
                const istGroessterDrop = i === groessterDropIdx;
                const farbe = istGroessterDrop
                  ? 'bg-red-100 border-red-300'
                  : i === stats.funnel.length - 1
                  ? 'bg-emerald-100 border-emerald-300'
                  : 'bg-blue-100 border-blue-300';
                return (
                  <div key={f.schritt} className="relative">
                    <div className={`relative h-12 rounded-lg border-2 ${farbe} overflow-hidden`}>
                      <div
                        className={`absolute inset-y-0 left-0 ${istGroessterDrop ? 'bg-red-200' : i === stats.funnel.length - 1 ? 'bg-emerald-200' : 'bg-blue-200'} transition-all`}
                        style={{ width: `${breite}%` }}
                      />
                      <div className="relative h-full flex items-center justify-between px-4">
                        <span className="font-bold text-gray-800 text-sm">
                          {i + 1}. {f.label}
                        </span>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="font-bold text-gray-900">{f.anzahl}</span>
                          {f.konversion_pct !== null && (
                            <span className={`text-xs ${istGroessterDrop ? 'text-red-700 font-bold' : 'text-gray-500'}`}>
                              {f.konversion_pct}% des vorherigen
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {f.drop_off_pct > 0 && i > 0 && (
                      <div className={`text-[11px] mt-0.5 ml-2 ${istGroessterDrop ? 'text-red-700 font-bold' : 'text-gray-400'}`}>
                        ↓ {f.drop_off_pct}% Abbruch zwischen Schritt {i} und {i + 1}
                        {istGroessterDrop && ' ← groesster Drop-Off im Funnel'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-6 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-gray-500 uppercase font-bold mb-1">Open → Submit</div>
                <div className="text-2xl font-bold text-gray-800">
                  {start > 0 ? ((stats.funnel[6]?.anzahl || 0) / start * 100).toFixed(1) : '0'}%
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase font-bold mb-1">Submit → DOI</div>
                <div className="text-2xl font-bold text-gray-800">
                  {(stats.funnel[6]?.anzahl || 0) > 0
                    ? ((stats.funnel[7]?.anzahl || 0) / stats.funnel[6].anzahl * 100).toFixed(1)
                    : '0'}%
                </div>
              </div>
            </div>
          </div>

          {/* Quelle-Verteilung */}
          {!quelleFilter && stats.quelle_verteilung && stats.quelle_verteilung.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 mt-6">
              <h3 className="text-sm font-bold text-gray-700 mb-3">Aufrufe pro Quelle</h3>
              <div className="space-y-1.5">
                {stats.quelle_verteilung.sort((a, b) => b.anzahl - a.anzahl).map(q => {
                  const pct = start > 0 ? (q.anzahl / start) * 100 : 0;
                  return (
                    <div key={q.quelle} className="flex items-center gap-3">
                      <span className="w-44 text-xs font-medium text-gray-700">{q.quelle}</span>
                      <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                        <div className="h-full bg-blue-400" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 w-16 text-right">{q.anzahl} ({pct.toFixed(0)}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ==========================================================================
// Kongress-Ticketlisten — Konsolidierung von Buchungs-Listen
// ==========================================================================
const KONGRESS_KATEGORIEN = [
  { id: '_alle', label: 'Alle' },
  { id: 'teilnehmer', label: 'Teilnehmer' },
  { id: 'aussteller', label: 'Aussteller' },
  { id: 'referent', label: 'Referenten' },
  { id: 'sponsor', label: 'Sponsoren' },
  { id: 'gast', label: 'Gaeste' },
  { id: 'unbezahlt', label: 'Unbezahlt' },
  { id: 'rechnungszahler', label: 'Rechnungszahler' },
];

function KongressView() {
  const [tab, setTab] = useState('liste'); // 'liste' | 'import'
  const [aktuelleKategorie, setAktuelleKategorie] = useState('_alle');
  const [suche, setSuche] = useState('');
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [counts, setCounts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Quellen-Liste (Import-History)
  const [quellen, setQuellen] = useState([]);
  const [quellenLoading, setQuellenLoading] = useState(false);

  // Mapping-State (nach Upload, vor Import-Bestaetigung)
  const [pending, setPending] = useState(null); // { quelle, spalten, vorschlag_mapping, vorschau_zeilen, zielfeld_labels }
  const [mapping, setMapping] = useState({});
  const [importDefaults, setImportDefaults] = useState({
    setze_kongressticket: false,
    setze_partyticket: false,
    setze_hotelbuchung: false,
    zusatz_kategorie_fuer_neue: '',
  });
  const [importLoading, setImportLoading] = useState(false);

  // Manueller Neu-/Edit-Modus
  const [editForm, setEditForm] = useState(null); // null oder TeilnehmerBody-Objekt

  const reload = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (suche.trim()) params.append('suche', suche.trim());
      const data = await api.get(`/api/kongress/teilnehmer?${params.toString()}`);
      setTeilnehmer(data.teilnehmer || []);
      setCounts(data.counts || {});
    } catch (e) {
      alert('Konnte Liste nicht laden: ' + (e?.detail || e?.message || 'unbekannt'));
    } finally {
      setIsLoading(false);
    }
  };

  // Backend liefert alle — Kategorie-Tab filtert clientseitig per
  // Membership im 'kategorien'-Array.
  const teilnehmerGefiltert = (aktuelleKategorie && aktuelleKategorie !== '_alle')
    ? teilnehmer.filter(t => (t.kategorien || []).includes(aktuelleKategorie))
    : teilnehmer;

  const reloadQuellen = async () => {
    setQuellenLoading(true);
    try {
      const data = await api.get('/api/kongress/quellen');
      setQuellen(Array.isArray(data) ? data : []);
    } catch { /* still */ }
    setQuellenLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { reloadQuellen(); }, []);
  // Suche debounced
  useEffect(() => {
    const t = setTimeout(() => reload(), 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [suche]);

  // --- Upload-Handler ----------------------------------------------------
  const fileInputRef = React.useRef(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadKategorie, setUploadKategorie] = useState('teilnehmer');
  const [uploadLoading, setUploadLoading] = useState(false);

  const handleUpload = async (datei) => {
    if (!datei) return;
    if (!uploadName.trim()) {
      alert('Bitte Bezeichnung fuer die Quelle eingeben (z.B. "Ticketliste Hauptkongress").');
      return;
    }
    setUploadLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', uploadName.trim());
      fd.append('default_kategorie', uploadKategorie);
      fd.append('datei', datei);
      const res = await fetch('/api/kongress/quellen/upload', {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPending(data);
      setMapping(data.vorschlag_mapping || {});
      setUploadName('');
      reloadQuellen();
    } catch (e) {
      alert('Upload fehlgeschlagen: ' + (e.message || 'unbekannt'));
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImportBestaetigen = async () => {
    if (!pending) return;
    setImportLoading(true);
    try {
      const r = await api.post(`/api/kongress/quellen/${pending.quelle.id}/import`, {
        mapping,
        default_kategorie: pending.quelle.default_kategorie,
        ...importDefaults,
        zusatz_kategorie_fuer_neue: importDefaults.zusatz_kategorie_fuer_neue || null,
      });
      alert(`Import fertig: ${r.anzahl_neu} neu, ${r.anzahl_zusammengefuehrt} mit bestehenden Eintraegen zusammengefuehrt.`);
      setPending(null);
      setMapping({});
      setImportDefaults({ setze_kongressticket: false, setze_partyticket: false, setze_hotelbuchung: false, zusatz_kategorie_fuer_neue: '' });
      await reload();
      await reloadQuellen();
      setTab('liste');
    } catch (e) {
      alert('Import fehlgeschlagen: ' + (e?.detail || e?.message || 'unbekannt'));
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportAbbrechen = async () => {
    if (!pending) return;
    if (!window.confirm('Upload verwerfen? Die Datei wird vom Server geloescht.')) return;
    try {
      await api.del(`/api/kongress/quellen/${pending.quelle.id}`);
    } catch { /* still */ }
    setPending(null);
    setMapping({});
    reloadQuellen();
  };

  // Reset-Funktion: loescht ALLE Kongressdaten — gedacht fuer
  // 'nochmal sauber von vorne anfangen' nach Mapping-Aenderungen, oder
  // den Cleanup nach Kongress-Ende. Zwei-Stufen-Confirm.
  const handleAlleDatenLoeschen = async () => {
    if (!window.confirm('ALLE Kongress-Teilnehmer und Importe loeschen?\n\nDas wischt Tabelle UND alle hochgeladenen Dateien. Nicht ruekgaengig zu machen.')) return;
    if (!window.confirm('Wirklich sicher?')) return;
    try {
      const r = await api.del('/api/kongress/alle-daten');
      alert(`Geloescht: ${r.geloescht_teilnehmer} Teilnehmer, ${r.geloescht_quellen} Quellen.`);
      await reload();
      await reloadQuellen();
    } catch (e) {
      alert('Loeschen fehlgeschlagen: ' + (e?.detail || 'unbekannt'));
    }
  };

  // --- Teilnehmer-Aktionen ----------------------------------------------
  // Multi-Select: Klick auf ein Badge fuegt die Kategorie hinzu oder
  // entfernt sie. Mindestens eine Kategorie muss bleiben — wir verweigern
  // das Entfernen der letzten und fragen lieber, was wirklich gewollt ist.
  const handleKategorieTogglen = async (teilnehmer, kategorie) => {
    const aktuelle = teilnehmer.kategorien || ['teilnehmer'];
    const istDrin = aktuelle.includes(kategorie);
    let neue;
    if (istDrin) {
      neue = aktuelle.filter(k => k !== kategorie);
      if (neue.length === 0) {
        alert('Mindestens eine Kategorie muss bleiben. Setze stattdessen die gewuenschte zuerst, dann nimm die andere raus.');
        return;
      }
    } else {
      neue = [...aktuelle, kategorie];
    }
    // Optimistic Update — Liste lokal aktualisieren, dann Server
    setTeilnehmer(prev => prev.map(t => t.id === teilnehmer.id ? { ...t, kategorien: neue } : t));
    try {
      await api.put(`/api/kongress/teilnehmer/${teilnehmer.id}`, { kategorien: neue });
      // Counts neu vom Server holen (lokal koennten wir auch, aber so robuster)
      await reload();
    } catch (e) {
      alert('Aenderung fehlgeschlagen: ' + (e?.detail || 'unbekannt'));
      await reload();
    }
  };

  const handleEditOeffnen = (t) => setEditForm({ ...t });
  const handleNeu = () => setEditForm({
    anrede: '', titel: '', vorname: '', nachname: '', email: '', telefon: '',
    firma: '', position: '', strasse: '', plz: '', ort: '', land: '',
    kategorien: ['teilnehmer'],
    hat_kongressticket: false, hat_partyticket: false, hat_hotelbuchung: false,
    hotel_name: '', hotel_check_in: '', hotel_check_out: '', notiz: '',
  });
  const handleEditSpeichern = async () => {
    if (!editForm) return;
    try {
      const body = { ...editForm };
      // Datumsfelder: leere Strings auf null setzen
      if (!body.hotel_check_in) body.hotel_check_in = null;
      if (!body.hotel_check_out) body.hotel_check_out = null;
      if (body.id) {
        await api.put(`/api/kongress/teilnehmer/${body.id}`, body);
      } else {
        await api.post('/api/kongress/teilnehmer', body);
      }
      setEditForm(null);
      await reload();
    } catch (e) {
      alert('Speichern fehlgeschlagen: ' + (e?.detail || 'unbekannt'));
    }
  };
  const handleTeilnehmerLoeschen = async (id) => {
    if (!window.confirm('Diesen Teilnehmer wirklich loeschen?')) return;
    try {
      await api.del(`/api/kongress/teilnehmer/${id}`);
      await reload();
      setSelectedId(null);
    } catch (e) {
      alert('Loeschen fehlgeschlagen: ' + (e?.detail || 'unbekannt'));
    }
  };

  const exportUrl = (typ) => {
    const params = new URLSearchParams();
    if (aktuelleKategorie && aktuelleKategorie !== '_alle') params.append('kategorie', aktuelleKategorie);
    return `/api/kongress/${typ}?${params.toString()}`;
  };

  return (
    <div className="max-w-6xl animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Ticketlisten Kongress</h2>
      <p className="text-gray-500 mb-6">
        Konsolidierte Liste aus Ticket-, Aussteller- und Referenten-Daten.
        Bewusst getrennt vom Pflege-Datenbestand.
      </p>

      {/* Sub-Nav */}
      <div className="flex gap-2 mb-4 border-b border-gray-200">
        <button
          onClick={() => setTab('liste')}
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
            tab === 'liste' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >Teilnehmer-Liste</button>
        <button
          onClick={() => setTab('import')}
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
            tab === 'import' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >Datei-Import</button>
      </div>

      {tab === 'liste' && (
        <>
          {/* Kategorie-Tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {KONGRESS_KATEGORIEN.map(k => {
              const n = counts[k.id] || 0;
              const aktiv = aktuelleKategorie === k.id;
              return (
                <button
                  key={k.id}
                  onClick={() => setAktuelleKategorie(k.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                    aktiv ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {k.label}
                  <span className={`text-xs px-2 py-0.5 rounded ${aktiv ? 'bg-blue-700' : 'bg-white text-gray-500'}`}>
                    {n}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input
                type="text"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Name, E-Mail, Firma, Ort..."
                className="w-full py-2 pl-9 pr-3 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div className="ml-auto flex gap-2">
              <button
                onClick={handleNeu}
                className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold py-2 px-3 rounded-lg shadow-sm"
              ><Plus className="w-4 h-4" /> Manuell hinzufuegen</button>
              <a
                href={exportUrl('export.xlsx')}
                className="flex items-center gap-1 bg-slate-800 hover:bg-slate-900 text-white text-sm font-bold py-2 px-3 rounded-lg shadow-sm"
                title="Excel-Export der aktuellen Auswahl"
              ><Download className="w-4 h-4" /> Excel</a>
              <a
                href={exportUrl('druckliste.pdf')}
                target="_blank" rel="noreferrer"
                className="flex items-center gap-1 bg-fuchsia-700 hover:bg-fuchsia-800 text-white text-sm font-bold py-2 px-3 rounded-lg shadow-sm"
                title="Druckliste als PDF (sortiert nach Nachnamen)"
              ><FileText className="w-4 h-4" /> Druckliste PDF</a>
            </div>
          </div>

          {/* Ausdruckslisten (schlank: Nachname/Vorname/Firma) */}
          <div className="flex flex-wrap items-center gap-2 mb-4 px-3 py-2 bg-fuchsia-50 border border-fuchsia-200 rounded-lg">
            <span className="text-xs font-bold text-fuchsia-900 uppercase tracking-wide">
              Ausdruckslisten
            </span>
            <a
              href="/api/kongress/ausdruckslisten.pdf?typ=teilnehmer"
              target="_blank" rel="noreferrer"
              className="flex items-center gap-1 bg-white hover:bg-fuchsia-100 text-fuchsia-900 text-sm font-semibold py-1.5 px-3 rounded-md border border-fuchsia-300"
              title="Teilnehmer — Nachname, Vorname, Firma"
            ><FileText className="w-4 h-4" /> Teilnehmer</a>
            <a
              href="/api/kongress/ausdruckslisten.pdf?typ=aussteller_sponsor"
              target="_blank" rel="noreferrer"
              className="flex items-center gap-1 bg-white hover:bg-fuchsia-100 text-fuchsia-900 text-sm font-semibold py-1.5 px-3 rounded-md border border-fuchsia-300"
              title="Aussteller & Sponsoren kombiniert, mit Kategorie-Spalten"
            ><FileText className="w-4 h-4" /> Aussteller & Sponsoren</a>
            <a
              href="/api/kongress/ausdruckslisten.pdf?typ=referent"
              target="_blank" rel="noreferrer"
              className="flex items-center gap-1 bg-white hover:bg-fuchsia-100 text-fuchsia-900 text-sm font-semibold py-1.5 px-3 rounded-md border border-fuchsia-300"
              title="Referenten — Nachname, Vorname, Firma"
            ><FileText className="w-4 h-4" /> Referenten</a>
            <a
              href="/api/kongress/ausdruckslisten.pdf?typ=gesamt"
              target="_blank" rel="noreferrer"
              className="flex items-center gap-1 bg-white hover:bg-fuchsia-100 text-fuchsia-900 text-sm font-semibold py-1.5 px-3 rounded-md border border-fuchsia-300"
              title="Gesamtliste — Nr., Kennung (T/A/S/R), Nachname, Vorname"
            ><FileText className="w-4 h-4" /> Gesamtliste</a>
          </div>

          {/* Tabelle */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {isLoading ? (
              <div className="text-center text-gray-400 py-10 text-sm">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Lade...
              </div>
            ) : teilnehmerGefiltert.length === 0 ? (
              <div className="text-center text-gray-400 py-10 text-sm">
                Keine Teilnehmer in dieser Kategorie. Im Reiter „Datei-Import" eine CSV/Excel hochladen.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left p-3 font-bold text-gray-600 text-xs uppercase">Name</th>
                    <th className="text-left p-3 font-bold text-gray-600 text-xs uppercase">Firma</th>
                    <th className="text-left p-3 font-bold text-gray-600 text-xs uppercase">E-Mail</th>
                    <th className="text-left p-3 font-bold text-gray-600 text-xs uppercase">Kategorien — klick zum togglen</th>
                    <th className="text-right p-3 font-bold text-gray-600 text-xs uppercase">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {teilnehmerGefiltert.map(t => (
                    <tr key={t.id} className="border-b border-gray-100 hover:bg-blue-50/30">
                      <td className="p-3">
                        <div className="font-medium text-gray-800">{t.nachname}, {t.vorname}</div>
                        {(t.anrede || t.titel) && (
                          <div className="text-xs text-gray-400">{[t.anrede, t.titel].filter(Boolean).join(' ')}</div>
                        )}
                        {(t.kaeufer_vorname || t.kaeufer_nachname) && (
                          <div className="text-[10px] mt-1 inline-block px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded border border-indigo-100">
                            gekauft von {t.kaeufer_vorname} {t.kaeufer_nachname}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-gray-700">{t.firma || '—'}</td>
                      <td className="p-3">
                        {t.email ? (
                          <a
                            href={`mailto:${t.email}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-sm break-all"
                            title="Mail-Programm oeffnen"
                          >{t.email}</a>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <KategorieBadges
                          aktuelle={t.kategorien || ['teilnehmer']}
                          onToggle={(k) => handleKategorieTogglen(t, k)}
                        />
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => handleEditOeffnen(t)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Bearbeiten"
                          ><Edit3 className="w-4 h-4" /></button>
                          <button
                            onClick={() => handleTeilnehmerLoeschen(t.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Loeschen"
                          ><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === 'import' && (
        <KongressImportTab
          pending={pending} mapping={mapping} setMapping={setMapping}
          importDefaults={importDefaults} setImportDefaults={setImportDefaults}
          importLoading={importLoading}
          handleImportBestaetigen={handleImportBestaetigen}
          handleImportAbbrechen={handleImportAbbrechen}
          uploadName={uploadName} setUploadName={setUploadName}
          uploadKategorie={uploadKategorie} setUploadKategorie={setUploadKategorie}
          uploadLoading={uploadLoading}
          fileInputRef={fileInputRef}
          handleUpload={handleUpload}
          quellen={quellen} quellenLoading={quellenLoading}
          handleAlleDatenLoeschen={handleAlleDatenLoeschen}
        />
      )}

      {/* Edit-Modal (auch fuer Neu-Eintrag) */}
      {editForm && (
        <KongressEditModal
          form={editForm}
          setForm={setEditForm}
          onSpeichern={handleEditSpeichern}
          onAbbrechen={() => setEditForm(null)}
        />
      )}
    </div>
  );
}


function KongressImportTab({ pending, mapping, setMapping, importDefaults, setImportDefaults,
                            importLoading, handleImportBestaetigen, handleImportAbbrechen,
                            uploadName, setUploadName, uploadKategorie, setUploadKategorie,
                            uploadLoading, fileInputRef, handleUpload,
                            quellen, quellenLoading,
                            handleAlleDatenLoeschen }) {
  if (pending) {
    return (
      <div className="bg-white rounded-xl border border-blue-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Spalten-Mapping pruefen</h3>
            <div className="text-sm text-gray-500 mt-0.5">
              Datei: <span className="font-medium">{pending.quelle.dateiname_original}</span> ·
              {' '}{pending.quelle.anzahl_zeilen} Zeilen ·
              Default-Kategorie: {pending.quelle.default_kategorie}
            </div>
          </div>
          <button
            onClick={handleImportAbbrechen}
            className="text-sm text-red-600 hover:underline"
          >Verwerfen</button>
        </div>

        <p className="text-sm text-gray-600 mb-3">
          Ordne jede Datei-Spalte einem Zielfeld zu (oder „ignorieren"). Wir haben einen Vorschlag gemacht — bitte pruefen.
        </p>

        {/* Mapping-Liste */}
        <div className="space-y-1.5 mb-6">
          {pending.spalten.map(sp => (
            <div key={sp} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
              <div className="flex-1 text-sm font-medium text-gray-800">{sp}</div>
              <div className="text-gray-300">→</div>
              <select
                value={mapping[sp] || 'ignorieren'}
                onChange={(e) => setMapping({ ...mapping, [sp]: e.target.value })}
                className="text-sm border border-gray-300 rounded px-2 py-1 bg-white w-56"
              >
                <option value="ignorieren">— ignorieren —</option>
                {Object.entries(pending.zielfeld_labels).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {/* Vorschau */}
        <div className="mb-6">
          <div className="text-xs font-bold text-gray-500 uppercase mb-2">Vorschau (erste 5 Zeilen)</div>
          <div className="overflow-x-auto border border-gray-200 rounded">
            <table className="text-xs">
              <thead className="bg-gray-50">
                <tr>{pending.spalten.map(sp => (
                  <th key={sp} className="text-left p-2 font-bold text-gray-600 whitespace-nowrap">{sp}</th>
                ))}</tr>
              </thead>
              <tbody>
                {pending.vorschau_zeilen.map((row, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    {pending.spalten.map(sp => (
                      <td key={sp} className="p-2 text-gray-700 whitespace-nowrap max-w-xs truncate">
                        {String(row[sp] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Globale Flags */}
        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
          <div className="text-xs font-bold text-amber-800 uppercase mb-2">Diese Datei enthaelt Buchungen fuer:</div>
          <div className="flex gap-4 text-sm mb-3">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={importDefaults.setze_kongressticket}
                onChange={(e) => setImportDefaults({ ...importDefaults, setze_kongressticket: e.target.checked })}
              /> Kongress-Ticket
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={importDefaults.setze_partyticket}
                onChange={(e) => setImportDefaults({ ...importDefaults, setze_partyticket: e.target.checked })}
              /> Party-Ticket
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={importDefaults.setze_hotelbuchung}
                onChange={(e) => setImportDefaults({ ...importDefaults, setze_hotelbuchung: e.target.checked })}
              /> Hotelbuchung
            </label>
          </div>
          <div className="border-t border-amber-200 pt-3">
            <div className="text-xs font-bold text-amber-800 uppercase mb-1">
              Zusatz-Kategorie nur fuer NEU angelegte Eintraege (Bestand bleibt unberuehrt):
            </div>
            <select
              value={importDefaults.zusatz_kategorie_fuer_neue || ''}
              onChange={(e) => setImportDefaults({ ...importDefaults, zusatz_kategorie_fuer_neue: e.target.value })}
              className="w-full text-sm border border-amber-300 rounded px-2 py-1.5 bg-white"
            >
              <option value="">— keine —</option>
              {KONGRESS_KATEGORIEN.filter(k => k.id !== '_alle').map(k => (
                <option key={k.id} value={k.id}>{k.label}</option>
              ))}
            </select>
            <div className="text-[11px] text-amber-700 mt-1">
              z.B. bei Aussteller-Datei: 'Unbezahlt' setzen, damit Aussteller, die noch kein Ticket gekauft haben, gleich als unbezahlt markiert sind. Bereits vorhandene Eintraege (mit Match) bekommen NUR die Default-Kategorie.
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={handleImportAbbrechen}
            className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-100"
          >Abbrechen</button>
          <button
            onClick={handleImportBestaetigen}
            disabled={importLoading}
            className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 shadow-sm"
          >{importLoading ? 'Importiere...' : 'Import durchfuehren'}</button>
        </div>
      </div>
    );
  }

  // Upload-UI + Quellen-History
  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-800 mb-3">Neue Datei hochladen</h3>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Bezeichnung</label>
            <input
              type="text"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              placeholder='z.B. "Ticketliste Hauptkongress"'
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Default-Kategorie</label>
            <select
              value={uploadKategorie}
              onChange={(e) => setUploadKategorie(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            >
              {KONGRESS_KATEGORIEN.filter(k => k.id !== '_alle').map(k => (
                <option key={k.id} value={k.id}>{k.label}</option>
              ))}
            </select>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => handleUpload(e.target.files?.[0])}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadLoading || !uploadName.trim()}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold py-2 px-4 rounded-lg shadow-sm disabled:opacity-60"
        >
          {uploadLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {uploadLoading ? 'Lade hoch...' : 'CSV/Excel waehlen'}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-gray-800">Bisherige Imports</h3>
          <button
            onClick={handleAlleDatenLoeschen}
            className="flex items-center gap-1 text-xs font-bold text-red-700 hover:bg-red-50 px-3 py-1.5 rounded border border-red-200"
            title="Alle Kongress-Teilnehmer + alle Quellen + alle Upload-Dateien loeschen"
          >
            <Trash2 className="w-3.5 h-3.5" /> Alle Daten zuruecksetzen
          </button>
        </div>
        {quellenLoading ? (
          <div className="text-center text-gray-400 py-6 text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Lade...
          </div>
        ) : quellen.length === 0 ? (
          <div className="text-sm text-gray-400 italic">Noch keine Dateien hochgeladen.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-2 font-bold text-gray-600 text-xs uppercase">Name</th>
                <th className="text-left p-2 font-bold text-gray-600 text-xs uppercase">Datei</th>
                <th className="text-left p-2 font-bold text-gray-600 text-xs uppercase">Default-Kat.</th>
                <th className="text-right p-2 font-bold text-gray-600 text-xs uppercase">Zeilen</th>
                <th className="text-right p-2 font-bold text-gray-600 text-xs uppercase">Neu</th>
                <th className="text-right p-2 font-bold text-gray-600 text-xs uppercase">Merged</th>
                <th className="text-left p-2 font-bold text-gray-600 text-xs uppercase">Datum</th>
                <th className="text-left p-2 font-bold text-gray-600 text-xs uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {quellen.map(q => (
                <tr key={q.id} className="border-b border-gray-100">
                  <td className="p-2 font-medium text-gray-800">{q.name}</td>
                  <td className="p-2 text-gray-500 text-xs">{q.dateiname_original}</td>
                  <td className="p-2 text-gray-600 capitalize">{q.default_kategorie}</td>
                  <td className="p-2 text-right text-gray-700">{q.anzahl_zeilen}</td>
                  <td className="p-2 text-right text-emerald-700 font-bold">{q.anzahl_neu || '—'}</td>
                  <td className="p-2 text-right text-blue-700">{q.anzahl_zusammengefuehrt || '—'}</td>
                  <td className="p-2 text-gray-500 text-xs">{q.created_at && new Date(q.created_at).toLocaleString('de-DE')}</td>
                  <td className="p-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                      q.status === 'importiert'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>{q.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}


// Externe Feld-Komponente — bewusst NICHT innerhalb von KongressEditModal
// definieren! Sonst entsteht bei jedem Tastendruck eine neue Funktions-
// Referenz, React remountet das Input und der Fokus geht verloren
// (= "nur ein Buchstabe, dann ist der Fokus weg"-Bug).
function Feld({ label, value, onChange, type = 'text', span = 1 }) {
  return (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <label className="text-xs font-bold text-gray-500">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
      />
    </div>
  );
}

function KongressEditModal({ form, setForm, onSpeichern, onAbbrechen }) {
  const istNeu = !form.id;
  const setF = (k, v) => setForm({ ...form, [k]: v });
  const aktuelleKats = form.kategorien || ['teilnehmer'];
  const [tab, setTab] = useState('kontakt'); // 'kontakt' | 'buchung' | 'hotel'
  const toggleKat = (k) => {
    const istDrin = aktuelleKats.includes(k);
    let neue;
    if (istDrin) {
      neue = aktuelleKats.filter(x => x !== k);
      if (neue.length === 0) return;
    } else {
      neue = [...aktuelleKats, k];
    }
    setF('kategorien', neue);
  };
  // Per Portal direkt unter document.body — sonst faengt der
  // 'animate-fade-in'-Container von KongressView das 'fixed'-Positioning
  // ein und macht es zu 'absolute' (=> grauer Overlay, kein Inhalt).
  return createPortal((
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-800">{istNeu ? 'Neuer Teilnehmer' : 'Teilnehmer bearbeiten'}</h3>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-6 pt-3 border-b border-gray-200">
          {[
            { id: 'kontakt', label: 'Kontakt & Kategorien' },
            { id: 'buchung', label: 'Buchung & Ticket' },
            { id: 'hotel', label: 'Hotel & Quellen' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-sm font-bold border-b-2 transition-colors ${
                tab === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >{t.label}</button>
          ))}
        </div>

        <div className="overflow-y-auto p-6 space-y-3 flex-1">
          {tab === 'kontakt' && (
            <>
              <div className="grid grid-cols-4 gap-3">
                <div><label className="text-xs font-bold text-gray-500">Anrede</label>
                  <select value={form.anrede || ''} onChange={(e) => setF('anrede', e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white">
                    <option value="">—</option><option>Frau</option><option>Herr</option><option>Divers</option>
                  </select>
                </div>
                <Feld label="Titel" value={form.titel} onChange={v => setF("titel", v)} />
                <Feld label="Vorname" value={form.vorname} onChange={v => setF("vorname", v)} />
                <Feld label="Nachname" value={form.nachname} onChange={v => setF("nachname", v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Feld label="E-Mail" value={form.email} onChange={v => setF("email", v)} type="email" />
                <Feld label="Telefon" value={form.telefon} onChange={v => setF("telefon", v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Feld label="Firma" value={form.firma} onChange={v => setF("firma", v)} />
                <Feld label="Position" value={form.position} onChange={v => setF("position", v)} />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <Feld label="Strasse" value={form.strasse} onChange={v => setF("strasse", v)} span={2} />
                <Feld label="PLZ" value={form.plz} onChange={v => setF("plz", v)} />
                <Feld label="Ort" value={form.ort} onChange={v => setF("ort", v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Feld label="Land" value={form.land} onChange={v => setF("land", v)} />
                <Feld label="Geburtsdatum" value={form.geburtsdatum} onChange={v => setF("geburtsdatum", v)} type="date" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 block mb-1">Kategorien (mehrere moeglich)</label>
                <KategorieBadges aktuelle={aktuelleKats} onToggle={toggleKat} />
              </div>
              <div><label className="text-xs font-bold text-gray-500">Notiz</label>
                <textarea value={form.notiz || ''} onChange={(e) => setF('notiz', e.target.value)}
                  rows={2} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" /></div>
            </>
          )}

          {tab === 'buchung' && (
            <>
              {/* Ticket-Pakete als Checkboxen */}
              <div className="flex gap-4 text-sm pb-2 border-b border-gray-100">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!form.hat_kongressticket}
                    onChange={(e) => setF('hat_kongressticket', e.target.checked)} /> Kongress-Ticket
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!form.hat_partyticket}
                    onChange={(e) => setF('hat_partyticket', e.target.checked)} /> Party-Ticket
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!form.hat_hotelbuchung}
                    onChange={(e) => setF('hat_hotelbuchung', e.target.checked)} /> Hotelbuchung
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Feld label="Ticketart" value={form.ticketart} onChange={v => setF("ticketart", v)} />
                <Feld label="Bestellart" value={form.bestellart} onChange={v => setF("bestellart", v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Feld label="Bestell-Code" value={form.bestellcode} onChange={v => setF("bestellcode", v)} />
                <Feld label="Transaktionscode" value={form.transaktionscode} onChange={v => setF("transaktionscode", v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Feld label="Sitzplatz" value={form.sitzplatz} onChange={v => setF("sitzplatz", v)} />
                <Feld label="Ticket-Status" value={form.ticket_status} onChange={v => setF("ticket_status", v)} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Feld label="Typ (Bestellung)" value={form.ticket_typ} onChange={v => setF("ticket_typ", v)} />
                <Feld label="Ticket-Anzahl" value={form.ticket_anzahl} onChange={v => setF("ticket_anzahl", v)} />
                <Feld label="Tickets gescannt" value={form.tickets_gescannt} onChange={v => setF("tickets_gescannt", v)} />
              </div>
              <Feld label="Gescannt am" value={form.gescannt_am} onChange={v => setF("gescannt_am", v)} />
              <div className="grid grid-cols-2 gap-3">
                <Feld label="Event" value={form.event_name} onChange={v => setF("event_name", v)} />
                <Feld label="Event-Startdatum" value={form.event_startdatum} onChange={v => setF("event_startdatum", v)} />
              </div>
              <div className="border-t border-gray-100 pt-3">
                <div className="text-xs font-bold text-gray-500 uppercase mb-2">Finanz / Zahlung</div>
                <div className="grid grid-cols-2 gap-3">
                  <Feld label="Gesamtbetrag (Brutto)" value={form.gesamtbetrag} onChange={v => setF("gesamtbetrag", v)} />
                  <Feld label="Gesamtbetrag Veranstalter" value={form.gesamtbetrag_veranstalter} onChange={v => setF("gesamtbetrag_veranstalter", v)} />
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <Feld label="Gutscheincode" value={form.gutscheincode} onChange={v => setF("gutscheincode", v)} />
                  <Feld label="Gutscheinbetrag" value={form.gutscheinbetrag} onChange={v => setF("gutscheinbetrag", v)} />
                  <Feld label="Zahldienstleister" value={form.zahldienstleister} onChange={v => setF("zahldienstleister", v)} />
                </div>
              </div>
              <div className="border-t border-gray-100 pt-3">
                <div className="text-xs font-bold text-gray-500 uppercase mb-2">
                  Kaeufer-Info (wenn vom Teilnehmer abweichend)
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Feld label="Kaeufer Vorname" value={form.kaeufer_vorname} onChange={v => setF("kaeufer_vorname", v)} />
                  <Feld label="Kaeufer Nachname" value={form.kaeufer_nachname} onChange={v => setF("kaeufer_nachname", v)} />
                </div>
              </div>
            </>
          )}

          {tab === 'hotel' && (
            <>
              <div className="bg-amber-50/40 p-3 rounded border border-amber-100">
                <div className="text-xs font-bold text-gray-500 uppercase mb-2">Hotel</div>
                <div className="grid grid-cols-3 gap-3">
                  <Feld label="Hotel-Name" value={form.hotel_name} onChange={v => setF("hotel_name", v)} />
                  <Feld label="Check-in" value={form.hotel_check_in} onChange={v => setF("hotel_check_in", v)} type="date" />
                  <Feld label="Check-out" value={form.hotel_check_out} onChange={v => setF("hotel_check_out", v)} type="date" />
                </div>
                <div className="mt-3">
                  <label className="text-xs font-bold text-gray-500">Adresse (Rechnung/Hotel, Freitext)</label>
                  <textarea value={form.rechnungsadresse || ''} onChange={(e) => setF('rechnungsadresse', e.target.value)}
                    rows={2} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                </div>
              </div>
              <div className="border-t border-gray-100 pt-3">
                <div className="text-xs font-bold text-gray-500 uppercase mb-2">Social</div>
                <div className="grid grid-cols-2 gap-3">
                  <Feld label="Social-Media" value={form.social_media} onChange={v => setF("social_media", v)} />
                  <Feld label="Facebook" value={form.facebook} onChange={v => setF("facebook", v)} />
                </div>
              </div>
              {Array.isArray(form.quellen_log) && form.quellen_log.length > 0 && (
                <div className="border-t border-gray-100 pt-3">
                  <div className="text-xs font-bold text-gray-500 uppercase mb-2">
                    Import-Herkunft ({form.quellen_log.length} Eintrag{form.quellen_log.length === 1 ? '' : 'e'})
                  </div>
                  <ul className="text-xs space-y-1">
                    {form.quellen_log.map((q, i) => (
                      <li key={i} className="bg-gray-50 px-2 py-1 rounded border border-gray-200">
                        <span className="font-medium text-gray-800">{q.quelle_name || '—'}</span>
                        {q.importiert_am && (
                          <span className="text-gray-400 ml-2">
                            {new Date(q.importiert_am).toLocaleString('de-DE')}
                          </span>
                        )}
                        {q.zeile_nr && (
                          <span className="text-gray-400 ml-2">Zeile {q.zeile_nr}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onAbbrechen}
            className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-200">Abbrechen</button>
          <button onClick={onSpeichern}
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-bold text-sm flex items-center gap-2">
            <Save className="w-4 h-4" />{istNeu ? 'Anlegen' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}


// Wiederverwendbare Badge-Reihe fuer Kongress-Kategorien — wird sowohl in
// der Tabelle (Inline-Toggle pro Person) als auch im Edit-Modal verwendet.
const KONGRESS_KAT_FARBEN = {
  teilnehmer:      { aktiv: 'bg-blue-600 text-white border-blue-600',         inaktiv: 'bg-white text-gray-500 border-gray-300 hover:bg-blue-50' },
  aussteller:      { aktiv: 'bg-emerald-600 text-white border-emerald-600',   inaktiv: 'bg-white text-gray-500 border-gray-300 hover:bg-emerald-50' },
  referent:        { aktiv: 'bg-fuchsia-600 text-white border-fuchsia-600',   inaktiv: 'bg-white text-gray-500 border-gray-300 hover:bg-fuchsia-50' },
  sponsor:         { aktiv: 'bg-amber-600 text-white border-amber-600',       inaktiv: 'bg-white text-gray-500 border-gray-300 hover:bg-amber-50' },
  gast:            { aktiv: 'bg-slate-600 text-white border-slate-600',       inaktiv: 'bg-white text-gray-500 border-gray-300 hover:bg-slate-50' },
  unbezahlt:       { aktiv: 'bg-red-600 text-white border-red-600',           inaktiv: 'bg-white text-gray-500 border-gray-300 hover:bg-red-50' },
  rechnungszahler: { aktiv: 'bg-indigo-600 text-white border-indigo-600',     inaktiv: 'bg-white text-gray-500 border-gray-300 hover:bg-indigo-50' },
};

function KategorieBadges({ aktuelle, onToggle }) {
  const set = new Set(aktuelle || []);
  return (
    <div className="flex flex-wrap gap-1.5">
      {KONGRESS_KATEGORIEN.filter(k => k.id !== '_alle').map(k => {
        const ist = set.has(k.id);
        const farbe = KONGRESS_KAT_FARBEN[k.id] || KONGRESS_KAT_FARBEN.teilnehmer;
        return (
          <button
            key={k.id}
            type="button"
            onClick={() => onToggle(k.id)}
            className={`text-xs font-bold px-2.5 py-1 rounded-full border transition-colors ${ist ? farbe.aktiv : farbe.inaktiv}`}
            title={ist ? `${k.label} — klick zum Entfernen` : `${k.label} — klick zum Hinzufuegen`}
          >
            {k.label}
          </button>
        );
      })}
    </div>
  );
}


// ==========================================================================
//  Doppelte Kunden zusammenfuehren (Merge)
// ==========================================================================
function KundenZusammenfuehren() {
  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState(null);

  const [zielId, setZielId] = useState(null);
  const [quelleId, setQuelleId] = useState(null);
  const [sucheZ, setSucheZ] = useState('');
  const [sucheQ, setSucheQ] = useState('');

  const [vorschau, setVorschau] = useState(null);
  const [vorschauLaedt, setVorschauLaedt] = useState(false);
  const [vorschauFehler, setVorschauFehler] = useState(null);
  const [entscheidungen, setEntscheidungen] = useState({});

  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);

  const ladeKunden = async () => {
    setLoading(true); setFehler(null);
    try {
      const liste = await api.get('/api/kunden');
      setKunden(liste || []);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      setFehler(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { ladeKunden(); }, []);

  const kundeById = (id) => kunden.find(k => k.id === id) || null;
  const label = (k) => k ? `${k.lastName || ''}, ${k.firstName || ''}`.trim() : '';
  const zusatz = (k) => {
    if (!k) return '';
    const t = [];
    if (k.dob) t.push(k.dob.split('-').reverse().join('.'));
    if (k.zip || k.city) t.push(`${k.zip || ''} ${k.city || ''}`.trim());
    if (k.insuranceNumber) t.push(`V-Nr ${k.insuranceNumber}`);
    return t.join(' · ');
  };

  const treffer = (q, exclude) => {
    const s = (q || '').trim().toLowerCase();
    if (s.length < 2) return [];
    return kunden.filter(k => {
      if (k.id === exclude) return false;
      const hay = `${k.firstName || ''} ${k.lastName || ''} ${k.zip || ''} ${k.city || ''} ${k.insuranceNumber || ''}`.toLowerCase();
      return hay.includes(s);
    }).slice(0, 8);
  };

  const rollenTauschen = () => {
    setZielId(quelleId); setQuelleId(zielId);
    setVorschau(null); setErgebnis(null);
  };

  const vorschauLaden = async () => {
    if (!zielId || !quelleId) return;
    setVorschauLaedt(true); setVorschauFehler(null); setVorschau(null); setErgebnis(null);
    try {
      const daten = await api.get(`/api/kunden/merge/vorschau?ziel_id=${encodeURIComponent(zielId)}&quelle_id=${encodeURIComponent(quelleId)}`);
      setVorschau(daten);
      const init = {};
      (daten.felder || []).forEach(f => { init[f.feld] = f.empfehlung || 'ziel'; });
      setEntscheidungen(init);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      setVorschauFehler(typeof msg === 'string' ? msg : 'Vorschau fehlgeschlagen.');
    } finally {
      setVorschauLaedt(false);
    }
  };

  const zusammenfuehren = async () => {
    if (!vorschau) return;
    const zName = vorschau.ziel.name, qName = vorschau.quelle.name;
    if (!confirm(
      `Zusammenführen bestätigen:\n\n` +
      `„${qName}“ wird in „${zName}“ übernommen.\n` +
      `Alle Termine, Mails, Protokolle, Aufgaben usw. wandern zu „${zName}“.\n\n` +
      `Das Profil „${qName}“ wird anschließend in den Papierkorb verschoben (30 Tage wiederherstellbar).\n\n` +
      `Fortfahren?`
    )) return;
    setLaeuft(true);
    try {
      const res = await api.post('/api/kunden/merge/ausfuehren', {
        ziel_id: zielId, quelle_id: quelleId, feld_entscheidungen: entscheidungen,
      });
      setErgebnis(res.statistik || {});
      setVorschau(null); setZielId(null); setQuelleId(null); setSucheZ(''); setSucheQ('');
      await ladeKunden();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || `HTTP ${err.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      alert(typeof msg === 'string' ? msg : 'Zusammenführung fehlgeschlagen.');
    } finally {
      setLaeuft(false);
    }
  };

  const summe = (obj) => Object.values(obj || {}).reduce((a, b) => a + b, 0);

  const PickPanel = ({ titel, farbe, hinweis, wert, setSuche, sucheWert, ausgewaehlt, onPick, exclude }) => (
    <div className="flex-1 min-w-0">
      <div className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: farbe }}>{titel}</div>
      <div className="text-xs text-gray-500 mb-2">{hinweis}</div>
      {ausgewaehlt ? (
        <div className="flex items-center justify-between gap-2 p-3 rounded-lg border-2" style={{ borderColor: farbe, background: '#fafafa' }}>
          <div className="min-w-0">
            <div className="font-bold text-gray-900 truncate">{label(ausgewaehlt)}</div>
            <div className="text-xs text-gray-500 truncate">{zusatz(ausgewaehlt) || '—'}</div>
          </div>
          <button onClick={() => onPick(null)} className="text-gray-400 hover:text-gray-700 shrink-0"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <div>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={wert}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Name, PLZ oder V-Nr tippen…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': farbe }}
            />
          </div>
          {sucheWert.trim().length >= 2 && (
            <div className="mt-1 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-auto">
              {treffer(sucheWert, exclude).length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400 italic">Keine Treffer.</div>
              ) : treffer(sucheWert, exclude).map(k => (
                <button key={k.id} onClick={() => onPick(k.id)} className="w-full text-left px-3 py-2 hover:bg-gray-50">
                  <div className="text-sm font-semibold text-gray-800">{label(k)}</div>
                  <div className="text-xs text-gray-500">{zusatz(k) || '—'}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-5xl animate-fade-in">
      <div className="flex items-center gap-2 mb-1">
        <GitMerge className="w-5 h-5" style={{ color: CI.brombeer }} />
        <h2 className="text-xl font-bold text-gray-900">Doppelte Kunden zusammenführen</h2>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Zwei Profile derselben Person zu einem verschmelzen. Alle verknüpften Daten (Termine, Mails,
        Protokolle, Aufgaben …) bleiben erhalten und wandern zum bleibenden Profil. Jede Zusammenführung
        wird in der Stammdatenhistorie protokolliert.
      </p>

      {loading && <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lädt Kunden…</div>}
      {fehler && <div className="text-red-600 text-sm">{fehler}</div>}

      {ergebnis && (
        <div className="mb-6 p-4 rounded-xl border border-green-200 bg-green-50">
          <div className="flex items-center gap-2 font-bold text-green-800 mb-1"><CheckCircle className="w-5 h-5" /> Zusammenführung erfolgreich</div>
          <div className="text-sm text-green-800">
            {summe(ergebnis) === 0 ? 'Keine verknüpften Datensätze umzuhängen.' : (
              <>Umgehängt: {Object.entries(ergebnis).map(([k, v]) => `${v} ${k}`).join(', ')}.</>
            )} Das Quell-Profil liegt jetzt im Papierkorb.
          </div>
        </div>
      )}

      {!loading && !fehler && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex flex-col md:flex-row items-stretch md:items-start gap-4">
            <PickPanel
              titel="Bleibt bestehen (Ziel)" farbe={CI.green}
              hinweis="Dieses Profil behält seine ID und wird ergänzt."
              wert={sucheZ} setSuche={(v) => { setSucheZ(v); }} sucheWert={sucheZ}
              ausgewaehlt={kundeById(zielId)}
              onPick={(id) => { setZielId(id); setVorschau(null); setErgebnis(null); }}
              exclude={quelleId}
            />
            <div className="flex md:flex-col items-center justify-center pt-6">
              <button onClick={rollenTauschen} disabled={!zielId || !quelleId}
                title="Rollen tauschen"
                className="p-2 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-30">
                <ArrowLeftRight className="w-4 h-4" />
              </button>
            </div>
            <PickPanel
              titel="Wird übernommen (Quelle)" farbe={CI.red}
              hinweis="Wandert nach dem Merge in den Papierkorb."
              wert={sucheQ} setSuche={(v) => { setSucheQ(v); }} sucheWert={sucheQ}
              ausgewaehlt={kundeById(quelleId)}
              onPick={(id) => { setQuelleId(id); setVorschau(null); setErgebnis(null); }}
              exclude={zielId}
            />
          </div>

          <div className="mt-5 flex justify-center">
            <button
              onClick={vorschauLaden}
              disabled={!zielId || !quelleId || vorschauLaedt}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-bold disabled:opacity-40"
              style={{ background: CI.brombeer }}
            >
              {vorschauLaedt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Vorschau anzeigen
            </button>
          </div>
        </div>
      )}

      {vorschauFehler && <div className="mt-4 text-red-600 text-sm">{vorschauFehler}</div>}

      {vorschau && (
        <div className="mt-6 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          {/* Zaehler + Warnung falls Quelle mehr Daten hat */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            {['ziel', 'quelle'].map(seite => {
              const d = vorschau[seite];
              const ist = seite === 'ziel';
              return (
                <div key={seite} className="p-3 rounded-lg border" style={{ borderColor: ist ? CI.green : CI.red }}>
                  <div className="text-xs font-bold uppercase" style={{ color: ist ? CI.green : CI.red }}>{ist ? 'Bleibt (Ziel)' : 'Quelle → Papierkorb'}</div>
                  <div className="font-bold text-gray-900">{d.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {Object.entries(d.verknuepft).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(' · ') || 'keine verknüpften Daten'}
                  </div>
                </div>
              );
            })}
          </div>

          {summe(vorschau.quelle.verknuepft) > summe(vorschau.ziel.verknuepft) && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Das Quell-Profil hat mehr verknüpfte Daten als das Ziel. Überlege, ob du die Rollen tauschen möchtest, damit das datenreichere Profil bestehen bleibt.</span>
            </div>
          )}

          {/* Feld-Entscheidungen */}
          {(vorschau.felder || []).length === 0 ? (
            <div className="text-sm text-gray-500 italic mb-4">Alle Stammdatenfelder sind identisch — es werden nur die verknüpften Daten übernommen.</div>
          ) : (
            <div className="mb-4">
              <div className="text-sm font-bold text-gray-700 mb-2">Abweichende Felder — bitte je Zeile den richtigen Wert wählen:</div>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                {vorschau.felder.map(f => (
                  <div key={f.feld} className="p-3">
                    <div className="text-xs font-bold text-gray-500 uppercase mb-2">{f.label}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {['ziel', 'quelle'].map(seite => {
                        const wert = seite === 'ziel' ? f.zielWert : f.quelleWert;
                        const gewaehlt = entscheidungen[f.feld] === seite;
                        const empfohlen = f.empfehlung === seite;
                        return (
                          <label key={seite}
                            className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${gewaehlt ? 'border-brombeer bg-fuchsia-50' : 'border-gray-200 hover:bg-gray-50'}`}
                            style={gewaehlt ? { borderColor: CI.brombeer } : {}}>
                            <input type="radio" name={`feld-${f.feld}`} value={seite}
                              checked={gewaehlt}
                              onChange={() => setEntscheidungen(prev => ({ ...prev, [f.feld]: seite }))} />
                            <span className="text-sm text-gray-800 min-w-0">
                              <span className="text-xs text-gray-400">{seite === 'ziel' ? 'Ziel' : 'Quelle'}: </span>
                              {wert ? wert : <span className="text-gray-400 italic">(leer)</span>}
                              {empfohlen && <span className="ml-1 text-xs font-bold" style={{ color: CI.green }}>· empfohlen</span>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {vorschau.notizen && (
            <div className="mb-4 p-3 rounded-lg bg-yellow-50 border border-yellow-100 text-sm text-gray-700">
              <div className="font-bold mb-1">Notizen werden zusammengeführt (beide bleiben erhalten):</div>
              <div className="whitespace-pre-wrap text-xs text-gray-600">{vorschau.notizen.zielNotiz || '(Ziel: leer)'}{"\n---\n"}{vorschau.notizen.quelleNotiz}</div>
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={zusammenfuehren} disabled={laeuft}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-bold disabled:opacity-40"
              style={{ background: CI.brombeer }}>
              {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
              Jetzt zusammenführen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
